import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { createHash } from "crypto";
import { seedDemoDataForUser } from "./seed.server";
import { extractMemoryFacts, generateSuggestionFor, regenerateForUser, parseContactsFromText, extractBioFields, transcribeAudio, summarizeRelationshipText, fetchToneSamples, draftNextTouchNote } from "./aria.server";
import { locationMatchesCity } from "./cities";
import { clampDailyTouches, maxDeepWatchFor, maxPeopleFor, planOf } from "./plans";

/**
 * How many more people this user may add before hitting their plan cap.
 * /pricing promises 5 on Free and 250 on Pro, so it has to be true.
 */
async function remainingPeopleSlots(supabase: any, userId: string) {
  const [{ data: profile }, { count }] = await Promise.all([
    supabase.from("profiles").select("plan").eq("id", userId).maybeSingle(),
    supabase.from("contacts").select("id", { count: "exact", head: true }).eq("user_id", userId),
  ]);
  const max = maxPeopleFor((profile as any)?.plan);
  return { max, used: count ?? 0, remaining: Math.max(0, max - (count ?? 0)) };
}

const peopleCapMessage = (max: number) =>
  `You're watching ${max} ${max === 1 ? "person" : "people"} — that's the limit on your plan. Upgrade to watch more.`;
import { eventLabel, expandMutedKinds, isKnownEventKind } from "./event-taxonomy";
import { loadCanonicalReason, loadCanonicalReasons } from "./canonical-reason.server";
import { isCoolingDown, rankHooks } from "./today-rank";
import { mentionsStaleYear } from "./source-quality";

function initialsFor(name: string) {
  return name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

const TONE_LEGACY: Record<string, { formality: number; warmth: number; length: "short" | "medium" | "long" }> = {
  casual:       { formality: 0, warmth: 3, length: "medium" },
  warm:         { formality: 1, warmth: 3, length: "medium" },
  neutral:      { formality: 2, warmth: 2, length: "medium" },
  polished:     { formality: 3, warmth: 2, length: "medium" },
  professional: { formality: 4, warmth: 1, length: "medium" },
};

const toneSchema = z.object({
  formality: z.number().int().min(0).max(4),
  warmth: z.number().int().min(0).max(4),
  length: z.enum(["short", "medium", "long"]),
});

function normalizeTone(raw: any): { formality: number; warmth: number; length: "short" | "medium" | "long" } {
  if (!raw) return { formality: 1, warmth: 2, length: "medium" };
  if (typeof raw === "string") return TONE_LEGACY[raw] ?? { formality: 1, warmth: 2, length: "medium" };
  const p = toneSchema.safeParse(raw);
  return p.success ? p.data : { formality: 1, warmth: 2, length: "medium" };
}

const DEFAULT_GROUPS = ["Family", "Professional", "Personal"] as const;

async function ensureDefaultGroupsFor(supabase: any, userId: string) {
  const { data: existing } = await supabase
    .from("categories")
    .select("id, name")
    .eq("user_id", userId);
  const have = new Set((existing ?? []).map((c: any) => c.name.toLowerCase()));
  const missing = DEFAULT_GROUPS.filter((n) => !have.has(n.toLowerCase()));
  if (missing.length) {
    await supabase.from("categories").insert(
      missing.map((name, i) => ({ user_id: userId, name, sort_order: i })),
    );
  }
}

async function writeContactGroups(
  supabase: any,
  userId: string,
  contactId: string,
  categoryIds: string[],
) {
  const ids = Array.from(new Set(categoryIds.filter(Boolean)));
  // Replace existing rows
  await supabase.from("contact_groups").delete().eq("contact_id", contactId).eq("user_id", userId);
  if (ids.length) {
    await supabase.from("contact_groups").insert(
      ids.map((category_id) => ({ user_id: userId, contact_id: contactId, category_id })),
    );
  }
  // Keep contacts.category_id as the "primary group" hint
  await supabase
    .from("contacts")
    .update({ category_id: ids[0] ?? null })
    .eq("id", contactId)
    .eq("user_id", userId);
}

export const getProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    if (error) throw error;
    return data;
  });

export const setDailyTouchTarget = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  // 999 is the "All" sentinel used by plans with unlimited daily touches.
  .inputValidator((input) => z.object({ target: z.number().int().min(1).max(999) }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error: readErr } = await supabase
      .from("profiles")
      .select("plan")
      .eq("id", userId)
      .maybeSingle();
    if (readErr) throw readErr;
    const target = clampDailyTouches(data.target, (row as any)?.plan);
    const { error } = await supabase
      .from("profiles")
      .update({ daily_touch_target: target } as any)
      .eq("id", userId);
    if (error) throw error;
    return { target };
  });

/**
 * What the caller has used against their plan: people, Deep Watch slots,
 * Boosts this month and the daily reach-out target. Every Pro teaser in the
 * app reads this so the numbers can never drift from what the server enforces.
 */
export const getPlanUsage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const monthStart = new Date();
    monthStart.setUTCDate(1);
    monthStart.setUTCHours(0, 0, 0, 0);

    const [{ data: profile }, { count: people }, { count: deepWatch }, { count: boosts }] =
      await Promise.all([
        supabase.from("profiles").select("plan, daily_touch_target").eq("id", userId).maybeSingle(),
        supabase.from("contacts").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase
          .from("contacts")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("is_vip", true),
        supabase
          .from("scan_runs")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("job_name", "boost-person")
          .gte("started_at", monthStart.toISOString()),
      ]);

    const plan = ((profile as any)?.plan ?? "free") as string;
    return {
      plan,
      people: people ?? 0,
      deepWatch: deepWatch ?? 0,
      boostsUsed: boosts ?? 0,
      dailyTarget: clampDailyTouches((profile as any)?.daily_touch_target ?? planOf(plan).defaultDailyTouches, plan),
    };
  });

export const completeOnboarding = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        cadence: z.enum(["light", "moderate", "high"]).optional(),
        goals: z.array(z.string()).max(10).optional(),
        categories: z.array(z.string()).max(20).optional(),
        display_name: z.string().trim().min(1).max(120).optional(),
        gmail_connected: z.boolean().optional(),
        professional_focus: z.string().trim().max(40).optional(),
        contacts: z
          .array(
            z.object({
              name: z.string().trim().min(1).max(120),
              email: z.string().trim().max(200).optional().nullable(),
              phone: z.string().trim().max(40).optional().nullable(),
              category: z.string().trim().max(60).optional().nullable(),
              company: z.string().trim().max(120).optional().nullable(),
              is_vip: z.boolean().optional(),
              birthday: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
            }),
          )
          .max(50)
          .optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;

    // Guard: don't re-run contact seeding if already onboarded.
    const { data: existingProfile } = await supabase
      .from("profiles")
      .select("onboarded")
      .eq("id", userId)
      .maybeSingle();
    const alreadyOnboarded = !!existingProfile?.onboarded;

    await supabase
      .from("profiles")
      .update({
        cadence: data.cadence ?? "moderate",
        goals: data.goals ?? [],
        onboarded: true,
        display_name: data.display_name ?? null,
        gmail_connected: data.gmail_connected ?? false,
        ...(data.professional_focus ? { professional_focus: data.professional_focus } : {}),
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId);

    const userContacts = data.contacts ?? [];
    if (userContacts.length > 0 && !alreadyOnboarded) {
      // Make sure any referenced categories exist
      const catNames = Array.from(
        new Set(userContacts.map((c) => c.category).filter((x): x is string => !!x)),
      );
      const catByName = new Map<string, string>();
      if (catNames.length) {
        const { data: existing } = await supabase
          .from("categories")
          .select("id, name")
          .eq("user_id", userId)
          .in("name", catNames);
        for (const c of existing ?? []) catByName.set(c.name as string, c.id as string);
        const missing = catNames.filter((n) => !catByName.has(n));
        if (missing.length) {
          const { data: inserted } = await supabase
            .from("categories")
            .insert(missing.map((name, i) => ({ user_id: userId, name, sort_order: i })))
            .select("id, name");
          for (const c of inserted ?? []) catByName.set(c.name as string, c.id as string);
        }
      }
      // Normalize emails and de-dupe within the batch (case-insensitive).
      const normalized = userContacts.map((c) => ({
        ...c,
        email: c.email ? c.email.trim().toLowerCase() : null,
      }));
      const seenInBatch = new Set<string>();
      const deduped = normalized.filter((c) => {
        if (!c.email) return true;
        if (seenInBatch.has(c.email)) return false;
        seenInBatch.add(c.email);
        return true;
      });
      // Skip emails already present for this user.
      const emails = deduped.map((c) => c.email).filter((e): e is string => !!e);
      let existingEmails = new Set<string>();
      if (emails.length) {
        const { data: existingContacts } = await supabase
          .from("contacts")
          .select("email")
          .eq("user_id", userId)
          .in("email", emails);
        existingEmails = new Set(
          (existingContacts ?? [])
            .map((r: any) => (r.email ? String(r.email).toLowerCase() : null))
            .filter((e: string | null): e is string => !!e),
        );
      }
      const cap = await remainingPeopleSlots(supabase, userId);
      const rows = deduped
        .filter((c) => !c.email || !existingEmails.has(c.email))
        .map((c) => ({
          user_id: userId,
          name: c.name,
          initials: initialsFor(c.name),
          email: c.email || null,
          phone: c.phone || null,
          company: c.company || null,
          is_vip: c.is_vip ?? false,
          category_id: (c.category && catByName.get(c.category)) || null,
          warmth: 50,
          priority: 60,
          cadence_days: 45,
          source: "onboarding",
          birthday: c.birthday || null,
          // Mark them queued at insert so the scheduled worker is a guaranteed
          // backstop: if the browser drops the in-flight enrichment when the
          // user moves on from onboarding, the server still finishes the job.
          enrichment_status: "queued",
        }))
        .slice(0, cap.remaining);
      if (rows.length) {
        await supabase.from("contacts").insert(rows);
      }
      // Generate initial hooks/suggestions for these new contacts
      try {
        if (rows.length) {
          await regenerateForUser(supabase, userId, Math.min(8, rows.length * 2));
        }
      } catch (e) {
        console.error("onboarding regenerate failed", e);
      }
    } else if (userContacts.length === 0 && !alreadyOnboarded) {
      // No real contacts provided — seed demo data so Today isn't empty
      await seedDemoDataForUser(supabase, userId, data.categories ?? []);
    }
    return { ok: true };
  });

export const getTodayQueue = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from("suggestions")
      .select(
        "id, reason, draft_message, tone, alternates, status, snooze_until, created_at, contact:contacts(id, name, email, phone, photo_url, initials, warmth, priority, is_vip, last_interaction_at, title, company, location, category:categories(id, name, color))",
      )
      .eq("user_id", userId)
      .eq("status", "pending")
      .or(`snooze_until.is.null,snooze_until.lte.${nowIso}`)
      .order("created_at", { ascending: true })
      .limit(10);
    if (error) throw error;
    return data ?? [];
  });

export const getPeople = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    await ensureDefaultGroupsFor(supabase, userId);
    const todayIso = new Date().toISOString().slice(0, 10);
    const sevenAgoIso = new Date(Date.now() - 7 * 86400000).toISOString();
    const fourteenAgoIso = new Date(Date.now() - 14 * 86400000).toISOString();
    const [
      { data: contacts, error: cErr },
      { data: cats, error: catErr },
      { data: trips },
      { data: pendingSugs },
      { data: freshSignals },
    ] = await Promise.all([
      supabase
        .from("contacts")
        .select("id, name, email, phone, photo_url, initials, warmth, priority, is_vip, last_interaction_at, cadence_days, birthday, title, company, location, category_id, created_at, groups:contact_groups(category_id)")
        .eq("user_id", userId),
      supabase
        .from("categories")
        .select("id, name, color, sort_order")
        .eq("user_id", userId)
        .order("sort_order"),
      supabase
        .from("trips")
        .select("city, starts_on, ends_on")
        .eq("user_id", userId)
        .or(`ends_on.is.null,ends_on.gte.${todayIso}`),
      supabase
        .from("suggestions")
        .select("contact_id, created_at")
        .eq("user_id", userId)
        .eq("status", "pending"),
      supabase
        .from("person_signals")
        .select("contact_id, detected_at")
        .eq("user_id", userId)
        .is("dismissed_at", null)
        .gte("detected_at", fourteenAgoIso),
    ]);
    if (cErr) throw cErr;
    if (catErr) throw catErr;
    const tripCities = Array.from(
      new Set((trips ?? []).map((t: any) => (t.city ?? "").trim().toLowerCase()).filter(Boolean)),
    );
    const sugMap = new Map<string, string>();
    for (const s of (pendingSugs ?? []) as any[]) {
      const prev = sugMap.get(s.contact_id);
      if (!prev || new Date(s.created_at).getTime() > new Date(prev).getTime()) {
        sugMap.set(s.contact_id, s.created_at);
      }
    }
    const signalMap = new Map<string, string>();
    for (const s of (freshSignals ?? []) as any[]) {
      const prev = signalMap.get(s.contact_id);
      if (!prev || new Date(s.detected_at).getTime() > new Date(prev).getTime()) {
        signalMap.set(s.contact_id, s.detected_at);
      }
    }
    // Active trip = currently in progress or starting within 14d.
    const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
    const activeTrip = ((trips ?? []) as any[]).find((t) => {
      if (!t.starts_on) return true;
      const s = new Date(t.starts_on);
      const e = t.ends_on ? new Date(t.ends_on) : s;
      return s <= new Date(todayMidnight.getTime() + 14 * 86400000) && e >= todayMidnight;
    }) as { city?: string } | undefined;
    void tripCities;
    const now = Date.now();
    function dueDays(c: any): number | null {
      if (!c.last_interaction_at) return null;
      const last = new Date(c.last_interaction_at).getTime();
      const cadence = (c.cadence_days ?? 45) * 86400000;
      return Math.round((now - (last + cadence)) / 86400000);
    }
    const shaped = ((contacts ?? []) as any[]).map((c) => {
      const dd = dueDays(c);
      const bdayDays = c.birthday ? daysUntilBirthday(c.birthday).days : null;
      const inTripCity = !!(c.location && activeTrip?.city && locationMatchesCity(c.location, activeTrip.city));
      const hasFreshSignal = signalMap.has(c.id);
      const hasDraftReady = !hasFreshSignal && sugMap.has(c.id);
      const addedRecently = !!(c.created_at && c.created_at >= sevenAgoIso);
      const cadence = c.cadence_days ?? 45;

      // Precedence: fresh_signal > draft_ready > birthday(≤7d) > trip > new_contact > vip_drift > drifting > on_track
      let trigger:
        | "fresh_signal"
        | "draft_ready"
        | "birthday"
        | "trip"
        | "new_contact"
        | "vip_drift"
        | "drifting"
        | "on_track"
        | null = null;
      let trigger_detail: string | null = null;

      if (hasFreshSignal) {
        trigger = "fresh_signal";
      } else if (hasDraftReady) {
        trigger = "draft_ready";
      } else if (bdayDays !== null && bdayDays <= 7) {
        trigger = "birthday";
        trigger_detail = bdayDays === 0 ? "today" : bdayDays === 1 ? "tomorrow" : `in ${bdayDays}d`;
      } else if (inTripCity && activeTrip?.city) {
        trigger = "trip";
        trigger_detail = activeTrip.city;
      } else if (!c.last_interaction_at && addedRecently) {
        trigger = "new_contact";
      } else if (c.is_vip && dd !== null && dd >= cadence) {
        trigger = "vip_drift";
        trigger_detail = `${dd}d`;
      } else if (dd !== null && dd >= 0) {
        trigger = "drifting";
        trigger_detail = `${dd}d`;
      } else if (dd !== null) {
        trigger = "on_track";
      }

      return {
        ...c,
        group_ids: (c.groups ?? []).map((g: any) => g.category_id) as string[],
        has_pending_suggestion: sugMap.has(c.id),
        trip_match: inTripCity,
        trigger,
        trigger_detail,
        days_overdue: dd,
        days_since: dd === null ? null : dd + (c.cadence_days ?? 45),
        due_days: dd,
        bday_days: bdayDays,
        trip_city: inTripCity ? activeTrip?.city ?? null : null,
      };
    });
    // Sort by trigger precedence; within bucket, by signal recency / urgency.
    const triggerRank: Record<string, number> = {
      fresh_signal: 0,
      draft_ready: 1,
      birthday: 2,
      trip: 3,
      new_contact: 4,
      vip_drift: 5,
      drifting: 6,
      on_track: 7,
    };
    shaped.sort((a: any, b: any) => {
      const ra = a.trigger ? triggerRank[a.trigger] : 7;
      const rb = b.trigger ? triggerRank[b.trigger] : 7;
      if (ra !== rb) return ra - rb;
      switch (a.trigger) {
        case "fresh_signal": {
          const ta = signalMap.get(a.id) ?? "";
          const tb = signalMap.get(b.id) ?? "";
          return tb.localeCompare(ta);
        }
        case "draft_ready": {
          const ta = sugMap.get(a.id) ?? "";
          const tb = sugMap.get(b.id) ?? "";
          return tb.localeCompare(ta);
        }
        case "birthday":
          return (a.bday_days ?? 99) - (b.bday_days ?? 99);
        case "trip":
          return String(a.name).localeCompare(String(b.name));
        case "vip_drift":
        case "drifting":
          return (b.due_days ?? 0) - (a.due_days ?? 0);
        case "on_track": {
          const ta = a.last_interaction_at ? new Date(a.last_interaction_at).getTime() : 0;
          const tb = b.last_interaction_at ? new Date(b.last_interaction_at).getTime() : 0;
          return tb - ta;
        }
        default: {
          if (!!b.is_vip !== !!a.is_vip) return b.is_vip ? 1 : -1;
          if ((b.priority ?? 0) !== (a.priority ?? 0)) return (b.priority ?? 0) - (a.priority ?? 0);
          return String(a.name).localeCompare(String(b.name));
        }
      }
    });
    return { contacts: shaped, categories: cats ?? [] };
  });

export const getContact = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const sigCutoff = new Date(Date.now() - 14 * 86400_000).toISOString();
    const { data: profileForSignals } = await supabase
      .from("profiles")
      .select("signal_mute_kinds")
      .eq("id", userId)
      .maybeSingle();
    const mutedKinds = Array.from(
      expandMutedKinds(
        Array.isArray(profileForSignals?.signal_mute_kinds)
          ? (profileForSignals!.signal_mute_kinds as string[])
          : [],
      ),
    );
    let sigQ = supabase
      .from("person_signals")
      .select("id, kind, summary, source_url, source_link_id, detected_at, confidence")
      .eq("user_id", userId)
      .eq("contact_id", data.contactId)
      .is("dismissed_at", null)
      .gte("confidence", 0.6)
      .gte("detected_at", sigCutoff)
      .order("detected_at", { ascending: false })
      .limit(1);
    if (mutedKinds.length) {
      sigQ = sigQ.not("kind", "in", `(${mutedKinds.map((k) => `"${k}"`).join(",")})`);
    }
    const [{ data: contact }, { data: memories }, { data: interactions }, { data: suggestion }, { data: voiceMemos }, { data: ctx }, { data: legacyFreshSignal }, { count: linkCount }, freshSignal] = await Promise.all([
      supabase
        .from("contacts")
        .select("*, category:categories(id, name, color), groups:contact_groups(category:categories(id, name, color))")
        .eq("user_id", userId)
        .eq("id", data.contactId)
        .maybeSingle(),
      supabase
        .from("memories")
        .select("id, fact, confidence, source, created_at")
        .eq("contact_id", data.contactId)
        .order("confidence", { ascending: false }),
      supabase
        .from("interactions")
        .select("id, type, summary, occurred_at")
        .eq("contact_id", data.contactId)
        .order("occurred_at", { ascending: false })
        .limit(20),
      supabase
        .from("suggestions")
        .select("id, reason, draft_message, tone, status")
        .eq("contact_id", data.contactId)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("voice_memos")
        .select("id, transcript, duration_sec, fact_count, created_at")
        .eq("contact_id", data.contactId)
        .order("created_at", { ascending: false }),
      supabase
        .from("contact_context")
        .select("current_company, role_title, interests, birthday_guess, birthday_guess_source")
        .eq("contact_id", data.contactId)
        .maybeSingle(),
      sigQ.maybeSingle(),
      supabase
        .from("contact_links")
        .select("id", { count: "exact", head: true })
        .eq("contact_id", data.contactId),
      loadCanonicalReason(supabase, userId, data.contactId),
    ]);
    void legacyFreshSignal;
    if (!contact) throw new Error("Contact not found");
    const enrichedContact = {
      ...contact,
      company: (contact as any).company ?? ctx?.current_company ?? null,
      title: (contact as any).title ?? ctx?.role_title ?? null,
      location: (contact as any).location ?? null,
      interests: Array.isArray((ctx as any)?.interests) ? (ctx as any).interests : [],
      birthday_guess: (ctx as any)?.birthday_guess ?? null,
      birthday_guess_source: (ctx as any)?.birthday_guess_source ?? null,
      link_count: linkCount ?? 0,
    };
    return { contact: enrichedContact, memories: memories ?? [], interactions: interactions ?? [], suggestion, voiceMemos: voiceMemos ?? [], freshSignal };
  });

export const actOnSuggestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        suggestionId: z.string().uuid(),
        action: z.enum(["send", "snooze_1d", "snooze_3d", "snooze_1w", "snooze_1m", "archive", "vip_toggle"]),
        draft: z.string().max(4000).optional(),
        channel: z.enum(["email", "sms", "whatsapp", "linkedin", "call", "in_person", "other"]).optional(),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: sug, error: gErr } = await supabase
      .from("suggestions")
      .select("id, contact_id, draft_message")
      .eq("id", data.suggestionId)
      .eq("user_id", userId)
      .maybeSingle();
    if (gErr) throw gErr;
    if (!sug) throw new Error("Suggestion not found");

    const now = new Date();
    if (data.action === "send") {
      await supabase
        .from("suggestions")
        .update({
          status: "sent",
          sent_at: now.toISOString(),
          draft_message: data.draft ?? sug.draft_message,
        })
        .eq("id", sug.id);
      const { logTouch: writeTouch } = await import("./touch-log.server");
      await writeTouch(supabase, {
        userId,
        contactId: sug.contact_id,
        channel: data.channel ?? "other",
        source: "reported",
        summary: (data.draft ?? sug.draft_message).slice(0, 200),
        occurredAt: now.toISOString(),
      });
    } else if (data.action.startsWith("snooze_")) {
      const days =
        data.action === "snooze_1d" ? 1
        : data.action === "snooze_3d" ? 3
        : data.action === "snooze_1m" ? 30
        : 7;
      const until = new Date(now.getTime() + days * 86400000);
      await supabase
        .from("suggestions")
        .update({ status: "snoozed", snooze_until: until.toISOString() })
        .eq("id", sug.id);
    } else if (data.action === "archive") {
      await supabase.from("suggestions").update({ status: "archived" }).eq("id", sug.id);
    } else if (data.action === "vip_toggle") {
      const { data: c } = await supabase
        .from("contacts")
        .select("is_vip")
        .eq("id", sug.contact_id)
        .maybeSingle();
      await supabase.from("contacts").update({ is_vip: !c?.is_vip }).eq("id", sug.contact_id);
    }
    return { ok: true };
  });

export const updateDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ suggestionId: z.string().uuid(), draft: z.string().max(4000) }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    await supabase
      .from("suggestions")
      .update({ draft_message: data.draft })
      .eq("id", data.suggestionId)
      .eq("user_id", userId);
    return { ok: true };
  });

export const createContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        name: z.string().trim().min(1).max(120),
        email: z.string().trim().email().max(200).optional().or(z.literal("")),
        phone: z.string().trim().max(40).optional().or(z.literal("")),
        title: z.string().trim().max(120).optional().or(z.literal("")),
        company: z.string().trim().max(120).optional().or(z.literal("")),
        location: z.string().trim().max(120).optional().or(z.literal("")),
        category_id: z.string().uuid().optional().nullable(),
        category_ids: z.array(z.string().uuid()).max(20).optional(),
        is_vip: z.boolean().optional(),
        cadence_days: z.number().int().min(1).max(365).optional(),
        birthday: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    await ensureDefaultGroupsFor(supabase, userId);
    const cap = await remainingPeopleSlots(supabase, userId);
    if (cap.remaining < 1) throw new Error(peopleCapMessage(cap.max));
    const groupIds = Array.from(
      new Set([...(data.category_ids ?? []), ...(data.category_id ? [data.category_id] : [])]),
    );
    const primary = groupIds[0] ?? null;
    const { data: row, error } = await supabase
      .from("contacts")
      .insert({
        user_id: userId,
        name: data.name,
        initials: initialsFor(data.name),
        email: data.email || null,
        phone: data.phone || null,
        title: data.title || null,
        company: data.company || null,
        location: data.location || null,
        category_id: primary,
        is_vip: data.is_vip ?? false,
        cadence_days: data.cadence_days ?? 45,
        warmth: 50,
        priority: data.is_vip ? 80 : 50,
        birthday: data.birthday || null,
      })
      .select("id")
      .single();
    if (error) throw error;
    if (groupIds.length) {
      await supabase.from("contact_groups").insert(
        groupIds.map((category_id) => ({ user_id: userId, contact_id: row.id, category_id })),
      );
    }
    return { id: row.id };
  });

export const setContactGroups = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({
      contactId: z.string().uuid(),
      categoryIds: z.array(z.string().uuid()).max(20),
    }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: owned } = await supabase
      .from("contacts").select("id").eq("id", data.contactId).eq("user_id", userId).maybeSingle();
    if (!owned) throw new Error("Contact not found");
    await writeContactGroups(supabase, userId, data.contactId, data.categoryIds);
    return { ok: true };
  });

export const createGroup = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ name: z.string().trim().min(1).max(40) }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: existing } = await supabase
      .from("categories").select("id, name, color, sort_order")
      .eq("user_id", userId).ilike("name", data.name).maybeSingle();
    if (existing) return existing;
    const { data: row, error } = await supabase
      .from("categories")
      .insert({ user_id: userId, name: data.name })
      .select("id, name, color, sort_order")
      .single();
    if (error) throw error;
    return row;
  });

export const deleteContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    // Cascade child rows manually since FKs aren't declared in schema.
    await supabase.from("memories").delete().eq("user_id", userId).eq("contact_id", data.contactId);
    await supabase.from("interactions").delete().eq("user_id", userId).eq("contact_id", data.contactId);
    await supabase.from("suggestions").delete().eq("user_id", userId).eq("contact_id", data.contactId);
    await supabase.from("reminders").delete().eq("user_id", userId).eq("contact_id", data.contactId);
    const { error } = await supabase.from("contacts").delete().eq("id", data.contactId).eq("user_id", userId);
    if (error) throw error;
    return { ok: true };
  });

export const renameGroup = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ categoryId: z.string().uuid(), name: z.string().trim().min(1).max(40) }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: clash } = await supabase
      .from("categories").select("id")
      .eq("user_id", userId).ilike("name", data.name).maybeSingle();
    if (clash && clash.id !== data.categoryId) throw new Error("A group with that name already exists");
    const { data: row, error } = await supabase
      .from("categories")
      .update({ name: data.name })
      .eq("id", data.categoryId)
      .eq("user_id", userId)
      .select("id, name, color, sort_order")
      .maybeSingle();
    if (error) throw error;
    if (!row) throw new Error("Group not found");
    return row;
  });

export const deleteGroup = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ categoryId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: owned } = await supabase
      .from("categories").select("id").eq("id", data.categoryId).eq("user_id", userId).maybeSingle();
    if (!owned) throw new Error("Group not found");
    await supabase.from("contact_groups").delete().eq("user_id", userId).eq("category_id", data.categoryId);
    await supabase.from("contacts").update({ category_id: null }).eq("user_id", userId).eq("category_id", data.categoryId);
    const { error } = await supabase.from("categories").delete().eq("id", data.categoryId).eq("user_id", userId);
    if (error) throw error;
    return { ok: true };
  });

export const updateContactCadence = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contactId: z.string().uuid(),
        cadenceDays: z.number().int().min(1).max(365),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("contacts")
      .update({ cadence_days: data.cadenceDays })
      .eq("id", data.contactId)
      .eq("user_id", userId);
    if (error) throw error;
    return { ok: true };
  });

/**
 * Star / unstar a person. Priority moves with it so scan tiering and
 * opportunity scoring agree with what the add-people flow already writes.
 */
export const setContactVip = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ contactId: z.string().uuid(), isVip: z.boolean() }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    // Deep Watch is the metered resource: a starred person is re-read many
    // times more often than the rest of the orbit. Enforce the plan's slot
    // count here, where every surface that stars someone passes through.
    if (data.isVip) {
      const [{ data: profile }, { count }] = await Promise.all([
        supabase.from("profiles").select("plan").eq("id", userId).maybeSingle(),
        supabase
          .from("contacts")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("is_vip", true),
      ]);
      const max = maxDeepWatchFor((profile as any)?.plan);
      if ((count ?? 0) >= max) {
        return {
          ok: false as const,
          is_vip: false,
          limit: max,
          error: `Deep Watch is full — your plan covers ${max} ${max === 1 ? "person" : "people"}. Unstar someone or upgrade.`,
        };
      }
    }
    const { error } = await supabase
      .from("contacts")
      .update({ is_vip: data.isVip, priority: data.isVip ? 80 : 50 })
      .eq("id", data.contactId)
      .eq("user_id", userId);
    if (error) throw error;
    // Deep Watch has to start tonight, not next week. The nightly link job
    // recomputes the tier correctly, but the *next check date* was frozen at
    // the old (weekly) cadence when the page was last read — so a freshly
    // starred person sat un-read for days. Pull their pages forward.
    if (data.isVip) {
      try {
        await supabase
          .from("contact_links")
          .update({ next_check_at: new Date().toISOString() })
          .eq("contact_id", data.contactId)
          .eq("user_id", userId)
          .eq("monitorable", true)
          .is("disabled_at", null);
      } catch (e) {
        console.error("setContactVip: could not pull links forward", e);
      }
      // Same for the web search: clearing the web-scan stamp makes them due
      // on the very next people run instead of whenever the old, slower
      // cadence happened to come round.
      try {
        await supabase
          .from("contacts")
          .update({ last_web_scan_at: null })
          .eq("id", data.contactId)
          .eq("user_id", userId);
      } catch (e) {
        console.error("setContactVip: could not pull web scan forward", e);
      }
    }

    return { ok: true as const, is_vip: data.isVip, limit: null, error: null };
  });

/** Fix a person's name (typos, formal → preferred). */
export const renameContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ contactId: z.string().uuid(), name: z.string().trim().min(1).max(120) }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const initials = data.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]!.toUpperCase())
      .join("");
    const { error } = await supabase
      .from("contacts")
      .update({ name: data.name, initials })
      .eq("id", data.contactId)
      .eq("user_id", userId);
    if (error) throw error;
    return { ok: true as const, name: data.name };
  });

/**
 * Correct a person's location / role / company by hand. Anything edited here
 * is recorded in `manual_fields` so the enrichment engine stops overwriting it.
 */
export const updateContactDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contactId: z.string().uuid(),
        location: z.string().trim().max(120).optional(),
        title: z.string().trim().max(120).optional(),
        company: z.string().trim().max(120).optional(),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: existing } = await supabase
      .from("contacts")
      .select("id, manual_fields")
      .eq("id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!existing) throw new Error("Contact not found");

    const update: Record<string, string | null> = {};
    const manual = new Set<string>(((existing as any).manual_fields as string[] | null) ?? []);
    for (const key of ["location", "title", "company"] as const) {
      const value = data[key];
      if (value === undefined) continue;
      update[key] = value === "" ? null : value;
      // Clearing a field hands it back to the engine; setting one locks it.
      if (value === "") manual.delete(key);
      else manual.add(key);
    }
    if (!Object.keys(update).length) return { ok: true as const };

    const { error } = await supabase
      .from("contacts")
      .update({ ...update, manual_fields: [...manual] })
      .eq("id", data.contactId)
      .eq("user_id", userId);
    if (error) throw error;
    return { ok: true as const };
  });

export const addMemory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contactId: z.string().uuid(),
        text: z.string().trim().min(2).max(2000),
        calendarEventId: z.string().uuid().optional(),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const text = data.text.trim();
    const looksAtomic = text.length < 140 && !/[.;]\s+\S/.test(text);

    let facts: Array<{ fact: string; confidence: number; source: string }>;
    if (looksAtomic) {
      facts = [{ fact: text, confidence: 0.9, source: "manual" }];
    } else {
      try {
        const extracted = await extractMemoryFacts(text);
        facts = extracted.length
          ? extracted.map((f) => ({ fact: f.fact, confidence: Math.max(0, Math.min(1, f.confidence ?? 0.7)), source: "ai" }))
          : [{ fact: text.slice(0, 200), confidence: 0.7, source: "manual" }];
      } catch (e) {
        console.error("memory extraction failed", e);
        facts = [{ fact: text.slice(0, 200), confidence: 0.7, source: "manual" }];
      }
    }

    const rows = facts.map((f) => ({ user_id: userId, contact_id: data.contactId, ...f }));
    const { error } = await supabase.from("memories").insert(rows);
    if (error) throw error;
    await supabase.from("interactions").insert({
      user_id: userId,
      contact_id: data.contactId,
      type: data.calendarEventId ? "meeting" : "note",
      summary: text.slice(0, 200),
      occurred_at: new Date().toISOString(),
      calendar_event_id: data.calendarEventId ?? null,
    });
    // What you just told us should change what we watch for.
    {
      const { registerFactTopics } = await import("@/lib/memory-topics.server");
      await registerFactTopics(supabase, userId, data.contactId, facts.map((f) => f.fact));
    }
    {
      const nowIso = new Date().toISOString();
      await supabase
        .from("contacts")
        .update({ last_interaction_at: nowIso })
        .eq("id", data.contactId)
        .eq("user_id", userId)
        .or(`last_interaction_at.is.null,last_interaction_at.lt.${nowIso}`);
    }
    return { count: facts.length };
  });

export const transcribeVoiceMemo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contactId: z.string().uuid(),
        audioBase64: z.string().min(20).max(8_000_000), // ~6MB raw audio
        mimeType: z.string().min(3).max(80),
        durationSec: z.number().int().min(0).max(600).optional(),
        calendarEventId: z.string().uuid().optional(),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    // Confirm contact belongs to user
    const { data: owned } = await supabase
      .from("contacts")
      .select("id")
      .eq("id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!owned) throw new Error("Contact not found");

    const transcript = (await transcribeAudio(data.audioBase64, data.mimeType)).trim();
    if (!transcript) return { transcript: "", count: 0 };

    let facts: Array<{ fact: string; confidence: number; source: string }>;
    try {
      const extracted = await extractMemoryFacts(transcript);
      facts = extracted.length
        ? extracted.map((f) => ({
            fact: f.fact,
            confidence: Math.max(0, Math.min(1, f.confidence ?? 0.7)),
            source: "voice",
          }))
        : [{ fact: transcript.slice(0, 200), confidence: 0.7, source: "voice" }];
    } catch (e) {
      console.error("voice extraction failed", e);
      facts = [{ fact: transcript.slice(0, 200), confidence: 0.7, source: "voice" }];
    }

    const rows = facts.map((f) => ({ user_id: userId, contact_id: data.contactId, ...f }));
    const { error } = await supabase.from("memories").insert(rows);
    if (error) throw error;
    await supabase.from("interactions").insert({
      user_id: userId,
      contact_id: data.contactId,
      type: data.calendarEventId ? "meeting" : "note",
      summary: `Voice memo: ${transcript.slice(0, 200)}`,
      occurred_at: new Date().toISOString(),
      calendar_event_id: data.calendarEventId ?? null,
    });
    {
      const { registerFactTopics } = await import("@/lib/memory-topics.server");
      await registerFactTopics(supabase, userId, data.contactId, facts.map((f) => f.fact));
    }
    {
      const nowIso = new Date().toISOString();
      await supabase
        .from("contacts")
        .update({ last_interaction_at: nowIso })
        .eq("id", data.contactId)
        .eq("user_id", userId)
        .or(`last_interaction_at.is.null,last_interaction_at.lt.${nowIso}`);
    }

    // Persist the audio so the user can play it back later.
    try {
      const ext = (data.mimeType.split("/")[1] || "webm").split(";")[0].replace(/[^a-z0-9]/gi, "") || "webm";
      const memoId =
        (globalThis.crypto as Crypto | undefined)?.randomUUID?.() ??
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const path = `${userId}/${memoId}.${ext}`;
      const bytes = Uint8Array.from(atob(data.audioBase64), (c) => c.charCodeAt(0));
      const up = await supabase.storage
        .from("voice-memos")
        .upload(path, bytes, { contentType: data.mimeType, upsert: false });
      if (up.error) throw up.error;
      const { error: insErr } = await supabase.from("voice_memos").insert({
        user_id: userId,
        contact_id: data.contactId,
        storage_path: path,
        mime_type: data.mimeType,
        duration_sec: data.durationSec ?? 0,
        transcript,
        fact_count: facts.length,
        calendar_event_id: data.calendarEventId ?? null,
      });
      if (insErr) throw insErr;
    } catch (e) {
      // Non-fatal: transcript+facts still saved even if audio persistence fails.
      console.error("voice memo persist failed", e);
    }

    return { transcript, count: facts.length, facts: facts.map((f) => f.fact) };
  });

export const getVoiceMemoUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ memoId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: memo, error } = await supabase
      .from("voice_memos")
      .select("storage_path")
      .eq("id", data.memoId)
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw error;
    if (!memo) throw new Error("Voice memo not found");
    const { data: signed, error: sErr } = await supabase.storage
      .from("voice-memos")
      .createSignedUrl(memo.storage_path, 60 * 5);
    if (sErr) throw sErr;
    return { url: signed.signedUrl };
  });

export const deleteVoiceMemo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ memoId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: memo } = await supabase
      .from("voice_memos")
      .select("storage_path")
      .eq("id", data.memoId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!memo) throw new Error("Voice memo not found");
    await supabase.storage.from("voice-memos").remove([memo.storage_path]);
    const { error } = await supabase.from("voice_memos").delete().eq("id", data.memoId).eq("user_id", userId);
    if (error) throw error;
    return { ok: true };
  });

export const regenerateForMe = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const generated = await regenerateForUser(supabase, userId, 6);
    return { generated };
  });

export const resetOnboarding = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ wipeData: z.boolean().optional() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    if (data.wipeData) {
      // Clear voice memo audio from storage first
      const { data: memos } = await supabase
        .from("voice_memos")
        .select("storage_path")
        .eq("user_id", userId);
      const paths = (memos ?? []).map((m: any) => m.storage_path).filter(Boolean);
      if (paths.length) {
        try { await supabase.storage.from("voice-memos").remove(paths); } catch {}
      }
      await supabase.from("voice_memos").delete().eq("user_id", userId);
      await supabase.from("memories").delete().eq("user_id", userId);
      await supabase.from("interactions").delete().eq("user_id", userId);
      await supabase.from("suggestions").delete().eq("user_id", userId);
      await supabase.from("hooks").delete().eq("user_id", userId);
      await supabase.from("hook_feedback").delete().eq("user_id", userId);
      await supabase.from("reminders").delete().eq("user_id", userId);
      await supabase.from("tone_samples").delete().eq("user_id", userId);
      await supabase.from("trips").delete().eq("user_id", userId);
      await supabase.from("contacts").delete().eq("user_id", userId);
      await supabase.from("categories").delete().eq("user_id", userId);
    }
    await supabase
      .from("profiles")
      .update({ onboarded: false, tours_seen: [], updated_at: new Date().toISOString() })
      .eq("id", userId);
    return { ok: true };
  });

export const regenerateSuggestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ suggestionId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: sug } = await supabase
      .from("suggestions")
      .select("id, contact_id, contact:contacts(id, name, title, company, location, cadence_days, last_interaction_at)")
      .eq("id", data.suggestionId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!sug || !sug.contact) throw new Error("Suggestion not found");
    const [{ data: mems }, { data: ints }, tone_samples] = await Promise.all([
      supabase.from("memories").select("fact").eq("contact_id", sug.contact_id).order("confidence", { ascending: false }).limit(5),
      supabase.from("interactions").select("type, summary, occurred_at").eq("contact_id", sug.contact_id).order("occurred_at", { ascending: false }).limit(3),
      fetchToneSamples(supabase, userId, sug.contact_id),
    ]);
    const out = await generateSuggestionFor({
      contact: sug.contact as any,
      memories: (mems ?? []).map((m: any) => m.fact),
      interactions: (ints ?? []) as any,
      tone_samples,
    });
    await supabase
      .from("suggestions")
      .update({
        reason: out.reason,
        draft_message: out.draft_message,
        tone: out.tone,
        alternates: out.alternates,
      })
      .eq("id", sug.id);
    return { ok: true };
  });


// ---- Bulk paste add ----

export const parseBulkContacts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ text: z.string().trim().min(2).max(8000) }).parse(i))
  .handler(async ({ data }) => {
    const parsed = await parseContactsFromText(data.text);
    return { contacts: parsed };
  });

export const bulkCreateContacts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contacts: z
          .array(
            z.object({
              name: z.string().trim().min(1).max(120),
              email: z.string().trim().max(200).optional(),
              phone: z.string().trim().max(40).optional(),
              title: z.string().trim().max(120).optional(),
              company: z.string().trim().max(120).optional(),
              hint: z.string().trim().max(200).optional(),
              category_id: z.string().uuid().optional().nullable(),
              category_ids: z.array(z.string().uuid()).max(8).optional(),
              is_vip: z.boolean().optional(),
              birthday: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
              location: z.string().trim().max(120).optional(),
              photo: z
                .string()
                .max(2_000_000) // ~1.5MB image as data URL
                .regex(/^data:image\/(png|jpe?g|gif|webp);base64,/i)
                .optional(),
              extra_notes: z.array(z.string().trim().min(1).max(280)).max(8).optional(),
            }),
          )
          .min(1)
          .max(30),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    // Dedupe against existing contacts (by email, case-insensitive; fall back to normalized name)
    const { data: existing } = await supabase
      .from("contacts")
      .select("name, email")
      .eq("user_id", userId);
    const normName = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");
    const existingEmails = new Set(
      (existing ?? []).map((r: any) => (r.email || "").trim().toLowerCase()).filter(Boolean),
    );
    const existingNames = new Set((existing ?? []).map((r: any) => normName(r.name || "")));

    const seenEmails = new Set<string>();
    const seenNames = new Set<string>();
    const accepted: typeof data.contacts = [];
    let skipped = 0;
    for (const c of data.contacts) {
      const em = (c.email || "").trim().toLowerCase();
      const nm = normName(c.name);
      const dupEmail = em && (existingEmails.has(em) || seenEmails.has(em));
      const dupName = !em && (existingNames.has(nm) || seenNames.has(nm));
      if (dupEmail || dupName) {
        skipped++;
        continue;
      }
      if (em) seenEmails.add(em);
      else seenNames.add(nm);
      accepted.push(c);
    }

    if (!accepted.length) return { count: 0, skipped, ids: [] as string[] };

    // Plan cap: take what fits, count the rest as skipped rather than failing.
    const cap = await remainingPeopleSlots(supabase, userId);
    if (cap.remaining < 1) throw new Error(peopleCapMessage(cap.max));
    if (accepted.length > cap.remaining) {
      skipped += accepted.length - cap.remaining;
      accepted.length = cap.remaining;
    }

    const rows = accepted.map((c) => ({
      user_id: userId,
      name: c.name,
      initials: initialsFor(c.name),
      email: c.email || null,
      phone: c.phone || null,
      title: c.title || null,
      company: c.company || null,
      location: c.location || null,
      category_id: c.category_id || (c.category_ids?.[0] ?? null),
      is_vip: c.is_vip ?? false,
      warmth: 50,
      priority: c.is_vip ? 80 : 50,
      cadence_days: 45,
      birthday: c.birthday || null,
    }));
    const { data: inserted, error } = await supabase
      .from("contacts")
      .insert(rows)
      .select("id, name");
    if (error) throw error;

    // Many-to-many groups via contact_groups
    const groupRows: Array<{ user_id: string; contact_id: string; category_id: string }> = [];
    (inserted ?? []).forEach((row, idx) => {
      const a = accepted[idx];
      if (!a) return;
      const ids = Array.from(
        new Set([...(a.category_ids ?? []), ...(a.category_id ? [a.category_id] : [])]),
      );
      for (const cid of ids) {
        groupRows.push({ user_id: userId, contact_id: row.id, category_id: cid });
      }
    });
    if (groupRows.length) await supabase.from("contact_groups").insert(groupRows);

    // Save hints as initial memories
    const memRows: Array<{ user_id: string; contact_id: string; fact: string; confidence: number; source: string }> = [];
    (inserted ?? []).forEach((row, idx) => {
      const hint = accepted[idx]?.hint;
      if (hint) memRows.push({ user_id: userId, contact_id: row.id, fact: hint, confidence: 0.8, source: "bulk-import" });
      const extras = accepted[idx]?.extra_notes ?? [];
      for (const fact of extras) {
        memRows.push({ user_id: userId, contact_id: row.id, fact, confidence: 0.9, source: "import:ios-contacts" });
      }
    });
    if (memRows.length) await supabase.from("memories").insert(memRows);

    // Upload any provided photos and set photo_url on the contacts
    await Promise.all(
      (inserted ?? []).map(async (row, idx) => {
        const photo = accepted[idx]?.photo;
        if (!photo) return;
        try {
          const url = await uploadContactPhotoFromDataUrl(supabase, userId, row.id, photo);
          if (url) await supabase.from("contacts").update({ photo_url: url, photo_source: "manual" }).eq("id", row.id);
        } catch (e) {
          console.error("contact photo upload failed", e);
        }
      }),
    );
    return { count: inserted?.length ?? 0, skipped, ids: (inserted ?? []).map((r: any) => r.id) as string[] };
  });

// ---- Contact photo upload ----

async function uploadContactPhotoFromDataUrl(
  supabase: any,
  userId: string,
  contactId: string,
  dataUrl: string,
): Promise<string | null> {
  const m = /^data:(image\/(png|jpe?g|gif|webp));base64,(.+)$/i.exec(dataUrl);
  if (!m) return null;
  const mime = m[1].toLowerCase();
  const ext = mime.split("/")[1].replace("jpeg", "jpg");
  const b64 = m[3];
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  if (bytes.byteLength > 5 * 1024 * 1024) throw new Error("Photo is too large (max 5MB)");
  // Unguessable path: the bucket is public, so the random token is the access control.
  const path = `${userId}/${contactId}-${crypto.randomUUID().replace(/-/g, "")}.${ext}`;
  const up = await supabase.storage
    .from("contact-photos")
    .upload(path, bytes, { contentType: mime, upsert: true });
  if (up.error) throw up.error;
  const { data: pub } = supabase.storage.from("contact-photos").getPublicUrl(path);
  return pub?.publicUrl ?? null;
}

export const uploadContactPhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contactId: z.string().uuid(),
        dataUrl: z
          .string()
          .max(8_000_000)
          .regex(/^data:image\/(png|jpe?g|gif|webp);base64,/i),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: owned } = await supabase
      .from("contacts")
      .select("id")
      .eq("id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!owned) throw new Error("Contact not found");
    const url = await uploadContactPhotoFromDataUrl(supabase, userId, data.contactId, data.dataUrl);
    if (!url) throw new Error("Invalid photo");
    // Cache-bust by appending the upload time
    const busted = `${url}?v=${Date.now()}`;
    const { error } = await supabase
      .from("contacts")
      .update({ photo_url: busted, photo_source: "manual" })
      .eq("id", data.contactId)
      .eq("user_id", userId);
    if (error) throw error;
    return { photo_url: busted };
  });

// ---- Paste-a-bio enrichment ----

export const enrichContactFromBio = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ contactId: z.string().uuid(), text: z.string().trim().min(10).max(5000) }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: existing } = await supabase
      .from("contacts")
      .select("id, title, company, location, manual_fields")
      .eq("id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!existing) throw new Error("Contact not found");

    const out = await extractBioFields(data.text);
    const update: { title?: string; company?: string; location?: string } = {};
    const manual = new Set<string>(((existing as any).manual_fields as string[] | null) ?? []);
    if (out.title && !existing.title && !manual.has("title")) update.title = out.title;
    if (out.company && !existing.company && !manual.has("company")) update.company = out.company;
    if (out.location && !existing.location && !manual.has("location")) update.location = out.location;
    if (Object.keys(update).length) {
      await supabase.from("contacts").update(update).eq("id", data.contactId);
    }
    if (out.facts.length) {
      await supabase.from("memories").insert(
        out.facts.map((f) => ({
          user_id: userId,
          contact_id: data.contactId,
          fact: f.fact,
          confidence: f.confidence,
          source: "bio",
        })),
      );
    }
    return { fields_updated: Object.keys(update), facts_added: out.facts.length };
  });

// ---- Reminders ----

export const createReminder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contactId: z.string().uuid(),
        title: z.string().trim().min(1).max(200),
        due_at: z.string().datetime(),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase.from("reminders").insert({
      user_id: userId,
      contact_id: data.contactId,
      title: data.title,
      due_at: data.due_at,
    });
    if (error) throw error;
    return { ok: true };
  });

export const getDueReminders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from("reminders")
      .select("id, title, due_at, contact:contacts(id, name, initials, photo_url)")
      .eq("user_id", userId)
      .eq("status", "pending")
      .lte("due_at", nowIso)
      .order("due_at", { ascending: true })
      .limit(20);
    if (error) throw error;
    return data ?? [];
  });

export const completeReminder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ id: z.string().uuid(), action: z.enum(["complete", "snooze_1d", "dismiss"]) }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    if (data.action === "snooze_1d") {
      const newDue = new Date(Date.now() + 86400000).toISOString();
      await supabase.from("reminders").update({ due_at: newDue }).eq("id", data.id).eq("user_id", userId);
    } else {
      await supabase
        .from("reminders")
        .update({ status: data.action === "complete" ? "completed" : "dismissed", completed_at: new Date().toISOString() })
        .eq("id", data.id)
        .eq("user_id", userId);
    }
    return { ok: true };
  });

// ---- Mode preference ----

export const setMode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ mode: z.enum(["personal", "professional"]) }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    await supabase.from("profiles").update({ mode: data.mode, updated_at: new Date().toISOString() }).eq("id", userId);
    return { ok: true };
  });

// ---- Birthdays ----

function daysUntilBirthday(birthday: string, now = new Date()): { days: number; nextDate: Date; turning: number | null } {
  // birthday stored as YYYY-MM-DD; sentinel year 0001 means year unknown
  const [yStr, mStr, dStr] = birthday.split("-");
  const y = Number(yStr);
  const m = Number(mStr) - 1;
  const d = Number(dStr);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let next = new Date(today.getFullYear(), m, d);
  if (next < today) next = new Date(today.getFullYear() + 1, m, d);
  const days = Math.round((next.getTime() - today.getTime()) / 86400000);
  const turning = y > 1 ? next.getFullYear() - y : null;
  return { days, nextDate: next, turning };
}

export const getUpcomingBirthdays = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ days: z.number().int().min(1).max(365).optional() }).parse(i ?? {}),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const horizon = data.days ?? 30;
    const { data: rows } = await supabase
      .from("contacts")
      .select("id, name, initials, photo_url, birthday, is_vip")
      .eq("user_id", userId)
      .not("birthday", "is", null);
    const out = (rows ?? [])
      .map((c: any) => {
        const info = daysUntilBirthday(c.birthday);
        return { ...c, days_until: info.days, next_date: info.nextDate.toISOString(), turning: info.turning };
      })
      .filter((c: any) => c.days_until <= horizon)
      .sort((a: any, b: any) => a.days_until - b.days_until);
    return out;
  });

// ---- Home digest ("Hook + Digest") ----

type HookTriggerType = "fresh_signal" | "birthday" | "trip" | "vip_drift" | "drift" | "new_contact";

export type Hook = {
  id: string;
  trigger_type: HookTriggerType;
  tier: "moment" | "maintenance";
  contact: any;
  reason_label: string;
  reason_detail: string;
  why: string;
  draft: string | null;
  tone: string | null;
  suggestion_id: string | null;
  trip_city?: string;
  /** True when there is no recorded interaction with this person yet. */
  never_contacted?: boolean;
  /** Total days since the last touch — the number the drift pill prints. */
  days_since?: number;
  /** Days past the contact's cadence — drives how loud the pill reads. */
  days_overdue?: number;
  signal_id?: string;
  signal_kind?: string;
  signal_summary?: string;
  signal_source_url?: string | null;
  signal_published_at?: string | null;
  signal_detected_at?: string | null;
  /** This person was snoozed, but news landed since — we woke them up. */
  snooze_breakthrough?: boolean;
  opportunity?: {
    id: string;
    headline: string;
    thesis: string | null;
    suggested_play: string | null;
    evidence: Array<{ url: string; published_at: string | null }>;
  };
};

/** The label that marks a snooze row as an indefinite mute. */
export const MUTE_REASON = "Muted by user";
/** A mute is a snooze that never expires on its own. */
const MUTE_DAYS = 3650;

export const recordHookFeedback = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contactId: z.string().uuid(),
        triggerType: z.string().max(40),
        action: z.enum([
          "snooze", "snooze_1d", "snooze_1w", "snooze_1m", "mute",
          "skip", "not_relevant", "remember",
        ]),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const snoozeDays =
      data.action === "snooze_1d" ? 1
      : data.action === "snooze_1m" ? 30
      : data.action === "mute" ? MUTE_DAYS
      : (data.action === "snooze" || data.action === "snooze_1w") ? 7
      : 0;
    await supabase.from("hook_feedback").insert({
      user_id: userId,
      contact_id: data.contactId,
      trigger_type: data.triggerType,
      action:
        data.action === "mute" ? "mute"
        : data.action.startsWith("snooze") ? "snooze"
        : data.action,
    });
    if (snoozeDays > 0) {
      const until = new Date(Date.now() + snoozeDays * 86400000).toISOString();
      await supabase.from("suggestions").insert({
        user_id: userId,
        contact_id: data.contactId,
        status: "snoozed",
        snooze_until: until,
        reason: data.action === "mute" ? MUTE_REASON : "snoozed",
        draft_message: "",
        tone: "warm",
      });
    }
    if (data.action === "not_relevant") {
      const { count } = await supabase
        .from("hook_feedback")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("contact_id", data.contactId)
        .eq("action", "not_relevant");
      if ((count ?? 0) >= 3) {
        const { data: c } = await supabase
          .from("contacts")
          .select("priority, cadence_days")
          .eq("id", data.contactId)
          .maybeSingle();
        if (c) {
          await supabase
            .from("contacts")
            .update({
              priority: Math.max(0, (c.priority ?? 50) - 20),
              cadence_days: Math.min(365, Math.round((c.cadence_days ?? 45) * 1.5)),
            })
            .eq("id", data.contactId);
        }
      }
    }
    return { ok: true };
  });

/**
 * A mis-tapped Skip silently reshuffles Today for a week. Undo removes the
 * feedback row it just wrote (and any snooze placeholder) for that person.
 */
export const undoHookFeedback = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({ contactId: z.string().uuid(), triggerType: z.string().max(40) }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: rows } = await supabase
      .from("hook_feedback")
      .select("id")
      .eq("user_id", userId)
      .eq("contact_id", data.contactId)
      .eq("trigger_type", data.triggerType)
      .order("created_at", { ascending: false })
      .limit(1);
    const id = rows?.[0]?.id;
    if (id) await supabase.from("hook_feedback").delete().eq("id", id).eq("user_id", userId);
    return { ok: true, undone: Boolean(id) };
  });

export const recordToneEdit = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contactId: z.string().uuid().optional().nullable(),
        original: z.string().max(4000).optional().nullable(),
        final: z.string().trim().min(1).max(4000),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    if (data.original && data.original.trim() === data.final.trim()) return { ok: true };
    await supabase.from("tone_samples").insert({
      user_id: userId,
      contact_id: data.contactId ?? null,
      source: "edit",
      original_draft: data.original ?? null,
      final_text: data.final,
    });
    return { ok: true };
  });

export const getHomeDigest = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const nowIso = new Date().toISOString();
    const sixtyAgoIso = new Date(Date.now() - 60 * 86400000).toISOString();
    const sevenAgoIso = new Date(Date.now() - 7 * 86400000).toISOString();
    const fourteenAgoIso = new Date(Date.now() - 14 * 86400000).toISOString();

    const [
      { data: contacts },
      { data: pending },
      { data: snoozedSugs },
      { data: reminders },
      { data: trips },
      { data: feedback },
      { data: signalRows },
      { data: profileRow },
      { data: impressionRows },
    ] = await Promise.all([
      supabase
        .from("contacts")
        .select("id, name, initials, photo_url, is_vip, last_interaction_at, last_touch_source, last_touch_channel, cadence_days, birthday, location, title, company, warmth, email, phone, created_at, enrichment_status, enrichment_stage, enrichment_started_at")
        .eq("user_id", userId),
      supabase
        .from("suggestions")
        .select("id, contact_id, reason, draft_message, tone, alternates, status, snooze_until, created_at")
        .eq("user_id", userId)
        .eq("status", "pending")
        .or(`snooze_until.is.null,snooze_until.lte.${nowIso}`),
      supabase
        .from("suggestions")
        .select("contact_id, snooze_until, reason, created_at")
        .eq("user_id", userId)
        .eq("status", "snoozed")
        .gt("snooze_until", nowIso),
      supabase
        .from("reminders")
        .select("id, due_at, status")
        .eq("user_id", userId)
        .eq("status", "pending")
        .lte("due_at", nowIso),
      supabase
        .from("trips")
        .select("id, city, starts_on, ends_on")
        .eq("user_id", userId),
      supabase
        .from("hook_feedback")
        .select("contact_id, trigger_type, action, created_at")
        .eq("user_id", userId)
        .gte("created_at", sixtyAgoIso),
      supabase
        .from("person_signals")
        .select("id, contact_id, kind, summary, source_url, detected_at, confidence")
        .eq("user_id", userId)
        .is("dismissed_at", null)
        .gte("confidence", 0.6)
        .gte("detected_at", fourteenAgoIso)
        .order("detected_at", { ascending: false })
        .limit(50),
      supabase
        .from("profiles")
        .select("signal_mute_kinds")
        .eq("id", userId)
        .maybeSingle(),
      supabase
        .from("today_impressions")
        .select("contact_id, surfaced_on")
        .eq("user_id", userId)
        .gte("surfaced_on", new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10)),
    ]);

    const allContacts = contacts ?? [];
    const canonicalReasons = await loadCanonicalReasons(
      supabase,
      userId,
      allContacts.map((c: any) => c.id),
    );
    const pendingByContact = new Map<string, any>();
    for (const s of pending ?? []) pendingByContact.set(s.contact_id, s);
    // Snooze is "quiet unless something happens": a genuinely new signal that
    // landed AFTER the snooze was set wakes the person back up. Mute never does.
    const snoozeInfo = new Map<string, { since: number; muted: boolean }>();
    for (const s of (snoozedSugs ?? []) as any[]) {
      snoozeInfo.set(s.contact_id, {
        since: s.created_at ? new Date(s.created_at).getTime() : 0,
        muted: s.reason === MUTE_REASON,
      });
    }

    const suppressed = new Set<string>();
    const recentlyIgnored = new Set<string>();
    for (const f of feedback ?? []) {
      const ageDays = (Date.now() - new Date(f.created_at).getTime()) / 86400000;
      const key = `${f.contact_id}|${f.trigger_type}`;
      // "Remember" is a decision, not a rejection: park this trigger for a
      // week (it's now a saved memory) but never penalise the person.
      if (f.action === "remember") {
        if (ageDays < 7) suppressed.add(key);
        continue;
      }
      if (f.action === "not_relevant" && ageDays < 60) suppressed.add(key);
      else if ((f.action === "snooze" || f.action === "skip") && ageDays < 7) suppressed.add(key);
      if (ageDays < 14) recentlyIgnored.add(f.contact_id);
    }
    const isSuppressed = (cid: string, t: HookTriggerType) => suppressed.has(`${cid}|${t}`);

    // "Seen but not acted" decay: count the days each contact has appeared on
    // Today since their last touch (or since they were added, if never touched).
    // A touch advances last_interaction_at and zeroes the count automatically.
    const surfacedStaleDays = new Map<string, number>();
    for (const c of allContacts) {
      const sinceTs = c.last_interaction_at
        ? new Date(c.last_interaction_at).getTime()
        : c.created_at
          ? new Date(c.created_at).getTime()
          : 0;
      const sinceDay = new Date(sinceTs); sinceDay.setHours(0, 0, 0, 0);
      let days = 0;
      for (const imp of impressionRows ?? []) {
        if (imp.contact_id !== c.id) continue;
        const d = new Date(imp.surfaced_on); d.setHours(0, 0, 0, 0);
        if (d > sinceDay) days += 1;
      }
      if (days > 0) surfacedStaleDays.set(c.id, days);
    }

    const now = Date.now();
    const today = new Date(); today.setHours(0,0,0,0);

    const candidates: Hook[] = [];
    const seenContacts = new Set<string>();
    const sugFor = (cid: string) => pendingByContact.get(cid);
    const pushHook = (h: Hook) => {
      if (seenContacts.has(h.contact.id)) return;
      const snooze = snoozeInfo.get(h.contact.id);
      let woke = false;
      if (snooze) {
        const stamp = (h as any).signal_published_at ?? (h as any).signal_detected_at ?? null;
        const newsAt = stamp ? new Date(stamp).getTime() : 0;
        woke =
          !snooze.muted &&
          h.trigger_type === "fresh_signal" &&
          newsAt > snooze.since;
        if (!woke) return;
      }
      if (isSuppressed(h.contact.id, h.trigger_type)) return;
      // Someone you just reached out to rests for a while. A dated moment
      // (fresh signal, birthday) still breaks through — that's the product.
      if (isCoolingDown(h as any)) return;
      seenContacts.add(h.contact.id);
      candidates.push({ ...h, never_contacted: !h.contact?.last_interaction_at, snooze_breakthrough: woke });
    };

    // Fresh person signals (moment) — THE WEDGE: real moments, not reminders.
    // Only kinds the taxonomy doesn't already name get a hand-written label.
    // A generic "New post" on top of a real acquisition made Today read as if
    // nothing had happened.
    const SIGNAL_LABELS: Record<string, string> = {
      launch: "Launched something",
    };
    const mutedKinds = new Set<string>(
      expandMutedKinds(
        Array.isArray(profileRow?.signal_mute_kinds)
          ? (profileRow!.signal_mute_kinds as string[])
          : [],
      ),
    );
    const contactById = new Map<string, any>(allContacts.map((c: any) => [c.id, c]));
    const sigSeen = new Set<string>();
    for (const sig of canonicalReasons.values()) {
      if (mutedKinds.has(sig.kind)) continue;
      if (sigSeen.has(sig.contact_id)) continue;
      const c = contactById.get(sig.contact_id);
      if (!c) continue;
      sigSeen.add(sig.contact_id);
      // Known taxonomy kinds get their real label; anything unrecognised stays generic.
      const label = isKnownEventKind(sig.kind)
        ? eventLabel(sig.kind)
        : (SIGNAL_LABELS[sig.kind] ?? "Fresh signal");
      const detail = String(sig.summary ?? "").slice(0, 160);
      const sug = sugFor(c.id);
      pushHook({
        id: `reason-${sig.id}`,
        trigger_type: "fresh_signal",
        tier: "moment",
        contact: c,
        reason_label: label,
        reason_detail: detail,
        why: `${sig.published_at ? "Published" : "Found"} ${new Date(sig.published_at ?? sig.detected_at).toLocaleDateString()}${sig.source_url ? ` from ${sig.source_url}` : ""}.`,
        draft: sug?.draft_message ?? null,
        tone: sug?.tone ?? null,
        suggestion_id: sug?.id ?? null,
        signal_id: sig.signal_id ?? sig.id,
        signal_kind: sig.kind,
        signal_summary: detail,
        signal_source_url: sig.source_url ?? null,
        signal_published_at: sig.published_at,
        signal_detected_at: sig.detected_at,
        opportunity: sig.opportunity_id ? {
          id: sig.opportunity_id,
          headline: sig.summary,
          thesis: sig.thesis,
          suggested_play: sig.suggested_play,
          evidence: sig.source_url ? [{ url: sig.source_url, published_at: sig.published_at }] : [],
        } : undefined,
      });
    }

    // Birthdays ≤7d (moment)
    const bdays = allContacts
      .filter((c: any) => c.birthday)
      .map((c: any) => ({ c, info: daysUntilBirthday(c.birthday) }))
      .filter((x: any) => x.info.days <= 7)
      .sort((a: any, b: any) => a.info.days - b.info.days);
    for (const { c, info } of bdays) {
      const when = info.days === 0 ? "today" : info.days === 1 ? "tomorrow" : `in ${info.days}d`;
      const sug = sugFor(c.id);
      pushHook({
        id: `bday-${c.id}`,
        trigger_type: "birthday",
        tier: "moment",
        contact: c,
        reason_label: `Birthday ${when}`,
        reason_detail: info.turning ? `Turns ${info.turning}` : `${c.name}'s birthday`,
        why: `Birthday ${when} (from contact record).`,
        draft: sug?.draft_message ?? null,
        tone: sug?.tone ?? null,
        suggestion_id: sug?.id ?? null,
      });
    }

    // Trip + city overlap (moment)
    const activeTrip = (trips ?? []).find((t: any) => {
      if (!t.starts_on) return true;
      const s = new Date(t.starts_on);
      const e = t.ends_on ? new Date(t.ends_on) : s;
      return s <= new Date(today.getTime() + 14 * 86400000) && e >= today;
    });
    let tripCount = 0;
    if (activeTrip) {
      const matched = allContacts.filter((c: any) => locationMatchesCity(c.location, activeTrip.city));
      tripCount = matched.length;
      for (const c of matched) {
        const sug = sugFor(c.id);
        pushHook({
          id: `trip-${activeTrip.id}-${c.id}`,
          trigger_type: "trip",
          tier: "moment",
          contact: c,
          reason_label: `In ${activeTrip.city}`,
          reason_detail: "They're local — grab coffee?",
          why: `Active trip to ${activeTrip.city} overlaps their location.`,
          draft: sug?.draft_message ?? null,
          tone: sug?.tone ?? null,
          suggestion_id: sug?.id ?? null,
          trip_city: activeTrip.city,
        });
      }
    }

    // New contact, no outreach yet (moment)
    for (const c of allContacts) {
      if (c.last_interaction_at) continue;
      if (!c.created_at || new Date(c.created_at).toISOString() < sevenAgoIso) continue;
      const sug = sugFor(c.id);
      pushHook({
        id: `new-${c.id}`,
        trigger_type: "new_contact",
        tier: "moment",
        contact: c,
        reason_label: "New — say hi",
        reason_detail: "You haven't reached out yet.",
        why: "Added recently with no outreach.",
        draft: sug?.draft_message ?? null,
        tone: sug?.tone ?? null,
        suggestion_id: sug?.id ?? null,
      });
    }

    // Drift ranking
    const overdueRanked = allContacts
      .map((c: any) => {
        if (!c.last_interaction_at) return { c, daysOverdue: (c.cadence_days ?? 45) * 2 };
        const days = Math.floor((now - new Date(c.last_interaction_at).getTime()) / 86400000);
        return { c, daysOverdue: days - (c.cadence_days ?? 45) };
      })
      .filter((x: any) => x.daysOverdue > 0)
      .sort((a: any, b: any) => b.daysOverdue - a.daysOverdue);

    // VIP drift > 2× cadence (maintenance)
    for (const { c, daysOverdue } of overdueRanked) {
      if (!c.is_vip) continue;
      if (daysOverdue < (c.cadence_days ?? 45)) continue;
      const sug = sugFor(c.id);
      const total = daysOverdue + (c.cadence_days ?? 45);
      pushHook({
        id: `vip-${c.id}`,
        trigger_type: "vip_drift",
        tier: "maintenance",
        contact: c,
        reason_label: "VIP · drifting",
        reason_detail: `${total}d since last touch`,
        days_since: total,
        days_overdue: daysOverdue,
        why: `Marked VIP, last touch ${total}d ago (cadence ${c.cadence_days ?? 45}d).`,
        draft: sug?.draft_message ?? null,
        tone: sug?.tone ?? null,
        suggestion_id: sug?.id ?? null,
      });
    }
    // Standard drift (maintenance)
    for (const { c, daysOverdue } of overdueRanked) {
      if (c.is_vip) continue;
      const sug = sugFor(c.id);
      pushHook({
        id: `drift-${c.id}`,
        trigger_type: "drift",
        tier: "maintenance",
        contact: c,
        reason_label: "Drifting",
        reason_detail: `${daysOverdue}d past your cadence`,
        days_since: daysOverdue + (c.cadence_days ?? 45),
        days_overdue: daysOverdue,
        why: `Past cadence by ${daysOverdue}d (cadence ${c.cadence_days ?? 45}d).`,
        draft: sug?.draft_message ?? null,
        tone: sug?.tone ?? null,
        suggestion_id: sug?.id ?? null,
      });
    }

    // Score every candidate instead of ordering by bucket, then rotate the
    // near-ties by day so the tail of the list gets a turn.
    const ranked = rankHooks(candidates as any, { now, recentlyIgnored, surfacedStaleDays }) as Hook[];
    const hooks = ranked.slice(0, 24);

    // Record that these contacts appeared on Today today. Idempotent via the
    // unique (user_id, contact_id, surfaced_on) constraint, so repeated loads
    // in a day don't inflate the stale-day count.
    const todayDate = new Date().toISOString().slice(0, 10);
    if (hooks.length) {
      await supabase
        .from("today_impressions")
        .upsert(
          hooks.map((h) => ({ user_id: userId, contact_id: h.contact.id, surfaced_on: todayDate })),
          { onConflict: "user_id,contact_id,surfaced_on", ignoreDuplicates: true },
        );
    }

    // Attach header extras (groups, interests, birthday guess) for just the
    // handful of contacts that surfaced — matches getContact's shape so the
    // shared person header renders identically on Today.
    if (hooks.length) {
      const hookIds = Array.from(new Set(hooks.map((h) => h.contact.id)));
      const [{ data: groupRows }, { data: ctxRows }] = await Promise.all([
        supabase
          .from("contact_groups")
          .select("contact_id, category:categories(id, name, color)")
          .eq("user_id", userId)
          .in("contact_id", hookIds),
        supabase
          .from("contact_context")
          .select("contact_id, interests, birthday_guess, birthday_guess_source")
          .eq("user_id", userId)
          .in("contact_id", hookIds),
      ]);
      const groupsByContact = new Map<string, any[]>();
      for (const g of (groupRows ?? []) as any[]) {
        const list = groupsByContact.get(g.contact_id) ?? [];
        list.push({ category: g.category ?? null });
        groupsByContact.set(g.contact_id, list);
      }
      const ctxByContact = new Map<string, any>();
      for (const r of (ctxRows ?? []) as any[]) ctxByContact.set(r.contact_id, r);
      for (const h of hooks) {
        const ctxRow = ctxByContact.get(h.contact.id);
        (h as any).contact = {
          ...h.contact,
          groups: groupsByContact.get(h.contact.id) ?? [],
          interests: ctxRow?.interests ?? [],
          birthday_guess: ctxRow?.birthday_guess ?? null,
          birthday_guess_source: ctxRow?.birthday_guess_source ?? null,
        };
      }
    }

    const empty =
      hooks.length === 0
        ? { kind: "empty" as const, reason: (allContacts.length === 0 ? "no_contacts" : "all_caught_up") as "no_contacts" | "all_caught_up" }
        : null;

    return {
      hooks,
      empty,
      tagline: "Never lose touch.",
      counts: {
        birthdays: bdays.length,
        people: allContacts.length,
        reminders: (reminders ?? []).length,
        reach_out: overdueRanked.length,
        trip: activeTrip ? { city: activeTrip.city, count: tripCount } : null,
      },
    };
  });

// ---- Relationship summary (plain-language memory layer) ----

export const summarizeRelationship = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid(), force: z.boolean().optional() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: contact } = await supabase
      .from("contacts")
      .select("id, name, title, company, location, relationship_summary, relationship_summary_at")
      .eq("id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!contact) throw new Error("Contact not found");

    // Cache for 7 days unless forced
    if (!data.force && contact.relationship_summary && contact.relationship_summary_at) {
      const ageDays = (Date.now() - new Date(contact.relationship_summary_at).getTime()) / 86400000;
      if (ageDays < 7) return { summary: contact.relationship_summary, cached: true };
    }

    const [{ data: mems }, { data: ints }, { data: ctx }] = await Promise.all([
      supabase.from("memories").select("fact").eq("contact_id", data.contactId).order("confidence", { ascending: false }).limit(12),
      supabase.from("interactions").select("type, summary, occurred_at").eq("contact_id", data.contactId).order("occurred_at", { ascending: false }).limit(6),
      supabase.from("contact_context").select("role_title, current_company, passions, active_projects, recent_milestones, partner_name, kids, pets").eq("contact_id", data.contactId).maybeSingle(),
    ]);

    const hasCtx = !!ctx && (
      ctx.role_title || ctx.current_company ||
      (Array.isArray(ctx.passions) && ctx.passions.length) ||
      (Array.isArray(ctx.active_projects) && ctx.active_projects.length) ||
      (Array.isArray(ctx.recent_milestones) && ctx.recent_milestones.length) ||
      ctx.partner_name ||
      (Array.isArray(ctx.kids) && ctx.kids.length) ||
      (Array.isArray(ctx.pets) && ctx.pets.length)
    );

    if (!mems?.length && !ints?.length && !hasCtx && !contact.title && !contact.company) {
      return { summary: "", cached: false, empty: true };
    }

    const summary = await summarizeRelationshipText({
      contact: contact as any,
      memories: (mems ?? []).map((m: any) => m.fact),
      interactions: (ints ?? []) as any,
      context_card: ctx as any,
    });

    await supabase
      .from("contacts")
      .update({ relationship_summary: summary, relationship_summary_at: new Date().toISOString() })
      .eq("id", data.contactId);

    return { summary, cached: false };
  });


export const wakeSuggestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ suggestionId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("suggestions")
      .update({ status: "pending", snooze_until: null })
      .eq("id", data.suggestionId)
      .eq("user_id", userId);
    if (error) throw error;
    return { ok: true };
  });

export const snoozeContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        contactId: z.string().uuid(),
        days: z.union([
          z.literal(1), z.literal(3), z.literal(7), z.literal(30), z.literal(MUTE_DAYS),
        ]),
        /** Mute = indefinite; not even breaking news wakes them. */
        mute: z.boolean().optional(),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const muted = data.mute === true || data.days === MUTE_DAYS;
    const days = muted ? MUTE_DAYS : data.days;
    const until = new Date(Date.now() + days * 86400000).toISOString();
    const reason = muted ? MUTE_REASON : "Snoozed by user";

    // Re-snoozing replaces any existing snooze instead of stacking a second row.
    const { data: pending } = await supabase
      .from("suggestions")
      .select("id")
      .eq("user_id", userId)
      .eq("contact_id", data.contactId)
      .in("status", ["pending", "snoozed"]);

    if (pending && pending.length) {
      await supabase
        .from("suggestions")
        .update({ status: "snoozed", snooze_until: until, reason })
        .in("id", pending.map((p: any) => p.id));
    } else {
      // No pending suggestion: insert a placeholder snoozed row so Today
      // suppresses this contact until `until`.
      await supabase.from("suggestions").insert({
        user_id: userId,
        contact_id: data.contactId,
        reason,
        draft_message: "",
        status: "snoozed",
        snooze_until: until,
      });
    }
    return { ok: true, until, muted };
  });

/**
 * "Wake now" — clears every snooze/mute row for one person so they can be
 * surfaced again immediately.
 */
export const wakeContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("suggestions")
      .update({ status: "pending", snooze_until: null })
      .eq("user_id", userId)
      .eq("contact_id", data.contactId)
      .eq("status", "snoozed");
    if (error) throw error;
    return { ok: true };
  });

/** Is this person snoozed or muted right now, and until when? */
export const getContactSnooze = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase
      .from("suggestions")
      .select("snooze_until, reason")
      .eq("user_id", userId)
      .eq("contact_id", data.contactId)
      .eq("status", "snoozed")
      .gt("snooze_until", new Date().toISOString())
      .order("snooze_until", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!row) return { snoozed: false as const, until: null, muted: false };
    return {
      snoozed: true as const,
      until: row.snooze_until as string,
      muted: row.reason === MUTE_REASON,
    };
  });

export const listSnoozedSuggestions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("suggestions")
      .select(
        "id, reason, draft_message, tone, status, snooze_until, created_at, contact:contacts(id, name, email, phone, photo_url, initials, is_vip, cadence_days)",
      )
      .eq("user_id", userId)
      .eq("status", "snoozed")
      .gt("snooze_until", new Date().toISOString())
      .order("snooze_until", { ascending: false });
    if (error) throw error;
    // One row per person — keep the latest snooze.
    const seen = new Set<string>();
    const unique = (data ?? []).filter((s: any) => {
      const id = s.contact?.id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    unique.sort((a: any, b: any) => String(a.snooze_until).localeCompare(String(b.snooze_until)));
    return { suggestions: unique };
  });

export const getNextTouchNote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({
    contactId: z.string().uuid(),
    tone: z.union([toneSchema, z.enum(["casual", "warm", "neutral", "polished", "professional"])]).optional(),
    hookTrigger: z.enum(["fresh_signal", "birthday", "trip", "new_contact", "vip_drift", "drift"]).optional(),
    hookTriggerDetail: z.string().max(240).optional(),
    // New callers send raw row UUIDs. Accept and normalize the old canonical
    // `signal:<uuid>` / `opportunity:<uuid>` shape too so an already-open
    // Today tab cannot crash while its client bundle refreshes.
    signalId: z.string().refine((value) => {
      const raw = value.replace(/^(signal|opportunity):/, "");
      return z.string().uuid().safeParse(raw).success;
    }, "Invalid signal identifier").optional(),
    // The deck merges person_signals and opportunities; the origin tells us
    // which table to resolve the pinned card from.
    signalOrigin: z.enum(["signal", "opportunity"]).optional(),
  }).transform((value) => {
    if (!value.signalId) return value;
    const syntheticOrigin = value.signalId.startsWith("opportunity:")
      ? "opportunity" as const
      : value.signalId.startsWith("signal:")
        ? "signal" as const
        : undefined;
    return {
      ...value,
      signalId: value.signalId.replace(/^(signal|opportunity):/, ""),
      signalOrigin: syntheticOrigin ?? value.signalOrigin,
    };
  }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: contact } = await supabase
      .from("contacts")
      .select("id, name, title, company, location, last_interaction_at, tone_preference, cached_note, cached_note_tone, cached_note_signal_hash, cached_note_at, last_sent_note, last_sent_at, cadence_days")
      .eq("id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!contact) return { note: "", tone: "warm" as const, missing: true };

    const [{ data: mems }, { data: ints }, { data: ctx }] = await Promise.all([
      supabase.from("memories").select("id, fact").eq("contact_id", data.contactId).order("confidence", { ascending: false }).limit(8),
      supabase.from("interactions").select("id, type, summary, occurred_at").eq("contact_id", data.contactId).order("occurred_at", { ascending: false }).limit(4),
      supabase.from("contact_context").select("role_title, current_company, passions, active_projects, recent_milestones, interests, updated_at").eq("contact_id", data.contactId).maybeSingle(),
    ]);

    // Recency-weighted memories + groups + links + voice memos.
    const [{ data: memsRecent }, { data: groupRows }, { data: linkRows }, { data: voiceRows }, { data: dismissedSignalRows }] = await Promise.all([
      supabase.from("memories").select("id, fact, created_at").eq("contact_id", data.contactId).order("created_at", { ascending: false }).limit(6),
      supabase.from("contact_groups").select("category:categories(name)").eq("contact_id", data.contactId),
      supabase.from("contact_links").select("id, url, kind, created_at").eq("contact_id", data.contactId).order("created_at", { ascending: false }).limit(5),
      supabase.from("voice_memos").select("id, transcript, created_at").eq("contact_id", data.contactId).order("created_at", { ascending: false }).limit(2),
      supabase.from("person_signals").select("source_url").eq("contact_id", data.contactId).eq("user_id", userId).not("dismissed_at", "is", null),
    ]);

    // Hybrid memory list: recent first, then high-confidence fillers, deduped, cap 10.
    const memMap = new Map<string, { id: string; fact: string }>();
    for (const m of (memsRecent ?? []) as any[]) memMap.set(m.id, { id: m.id, fact: m.fact });
    for (const m of (mems ?? []) as any[]) if (!memMap.has(m.id)) memMap.set(m.id, { id: m.id, fact: m.fact });
    const memList = Array.from(memMap.values()).slice(0, 10);

    const groups = ((groupRows ?? []) as any[])
      .map((g) => g?.category?.name)
      .filter((n): n is string => typeof n === "string" && n.length > 0);

    // Pull scrape_cache snippets for the link urls (best-effort, may be empty).
    let linkSnippets: Array<{ url: string; kind: string; snippet: string | null }> = [];
    if (linkRows && linkRows.length) {
      const urls = (linkRows as any[]).map((l) => l.url);
      const { data: scrapes } = await supabase
        .from("scrape_cache")
        .select("url, markdown, fetched_at")
        .in("url", urls);
      const byUrl = new Map<string, string>();
      for (const s of (scrapes ?? []) as any[]) {
        if (typeof s.markdown === "string" && s.markdown.length) {
          byUrl.set(s.url, s.markdown.slice(0, 600));
        }
      }
      linkSnippets = (linkRows as any[]).map((l) => ({
        url: l.url,
        kind: l.kind,
        snippet: byUrl.get(l.url) ?? null,
      }));
    }

    // Fresh link changes detected by the watcher (last 7 days).
    const freshLinkCutoff = new Date(Date.now() - 7 * 86400_000).toISOString();
    const { data: freshChanges } = await supabase
      .from("link_snapshots")
      .select("url, change_summary, fetched_at, link_id")
      .eq("contact_id", data.contactId)
      .eq("is_change", true)
      .gte("fetched_at", freshLinkCutoff)
      .order("fetched_at", { ascending: false })
      .limit(3);

    // Fresh person_signals detected by the monitor-people cron (last 14 days).
    const freshSignalCutoff = new Date(Date.now() - 14 * 86400_000).toISOString();
    const { data: freshSignals } = await supabase
      .from("person_signals")
      .select("id, kind, summary, source_url, detected_at")
      .eq("contact_id", data.contactId)
      .eq("user_id", userId)
      .is("dismissed_at", null)
      // No kind whitelist: it silently dropped funding, promotions and every
      // kind added to the taxonomy since. Muting is the user-facing filter.
      .gte("detected_at", freshSignalCutoff)
      .order("detected_at", { ascending: false })
      .limit(3);
    const canonicalReason = await loadCanonicalReason(supabase, userId, data.contactId);

    // A pinned signal only counts if it belongs to this contact and is live.
    let pinnedSignal: { id: string; kind: string; summary: string; published_at: string | null; detected_at: string } | null = null;
    if (data.signalId && data.signalOrigin === "opportunity") {
      const { data: opp } = await supabase
        .from("opportunities")
        .select("id, event_kind, headline, thesis, created_at, surfaced_at, person_signal_id")
        .eq("id", data.signalId)
        .eq("contact_id", data.contactId)
        .eq("user_id", userId)
        .maybeSingle();
      const summary = [(opp as any)?.headline, (opp as any)?.thesis]
        .filter((s) => typeof s === "string" && s.trim().length > 0)
        .join(" — ");
      if (opp && summary.trim().length > 10) {
        pinnedSignal = {
          id: (opp as any).id,
          kind: (opp as any).event_kind ?? "news",
          summary,
          published_at: (opp as any).surfaced_at ?? null,
          detected_at: (opp as any).created_at,
        };
      }
    } else if (data.signalId) {
      const { data: pinned } = await supabase
        .from("person_signals")
        .select("id, kind, summary, published_at, detected_at")
        .eq("id", data.signalId)
        .eq("contact_id", data.contactId)
        .eq("user_id", userId)
        .is("dismissed_at", null)
        .maybeSingle();
      if (pinned && typeof pinned.summary === "string" && pinned.summary.trim().length > 10) {
        pinnedSignal = pinned as any;
      }
    }
    const usePinned = !!pinnedSignal && pinnedSignal.id !== (canonicalReason?.signal_id ?? null);

    const voiceMemos = ((voiceRows ?? []) as any[])
      .filter((v) => typeof v.transcript === "string" && v.transcript.trim().length > 0)
      .map((v) => ({ transcript: v.transcript.slice(0, 400), created_at: v.created_at }));

    const interests: string[] = Array.isArray((ctx as any)?.interests)
      ? ((ctx as any).interests as any[]).map((i) => String(i)).filter((s) => s.length > 0).slice(0, 12)
      : [];

    const hasAny =
      memList.length > 0 ||
      (ints?.length ?? 0) > 0 ||
      voiceMemos.length > 0 ||
      !!contact.title ||
      !!contact.company ||
      !!ctx;
    if (!hasAny) return { note: "", empty: true };

    const { data: profile } = await supabase
      .from("profiles")
      .select("tone_preference, full_name, display_name, professional_focus")
      .eq("id", userId)
      .maybeSingle();
    const tone = normalizeTone(
      data.tone ?? (contact as any).tone_preference ?? profile?.tone_preference,
    );
    const sender = {
      name: (profile as any)?.display_name || (profile as any)?.full_name || null,
      role: (profile as any)?.professional_focus || null,
    };
    const toneSamples = await fetchToneSamples(supabase, userId, data.contactId);

    // ---- Trigger picker (deterministic) ----
    const now = Date.now();
    const daySince = (iso?: string | null) => iso ? Math.floor((now - new Date(iso).getTime()) / 86400000) : Infinity;
    const ctxAny = (ctx ?? {}) as any;
    const milestones: any[] = Array.isArray(ctxAny.recent_milestones) ? ctxAny.recent_milestones : [];
    const dismissedSignalSources = new Set(
      ((dismissedSignalRows ?? []) as Array<{ source_url: string | null }>)
        .map((row) => row.source_url)
        .filter((url): url is string => typeof url === "string" && url.length > 0),
    );
    const lastSent = (contact as any).last_sent_note as string | null;
    const lastSentDays = daySince((contact as any).last_sent_at);
    const cadenceDays = (contact as any).cadence_days ?? 45;
    const daysSinceLast = daySince(contact.last_interaction_at);

    type Trigger = { kind: "unfinished_thread" | "fresh_link" | "fresh_signal" | "recent_milestone" | "time_window" | "birthday" | "trip_overlap" | "new_intro" | "mutual_interest" | "cadence_due" | "check_in"; detail: string };
    let trigger: Trigger | null = null;
    // The headline the note is written about, echoed back so the card can show
    // "Re: <headline>" and prove the draft matches the signal above it.
    let subjectHeadline: string | null = null;

    // 1) Unfinished thread from a recent voice memo (≤14d) — user mentioned an action
    const freshVoice = voiceMemos.find((v) => daySince(v.created_at) <= 14);
    if (freshVoice && /\b(i|i'?ve|i'?ll)\s+(sent|told|messaged|emailed|congratulat|promised|said|wrote|pinged|texted|reached out|introduced)/i.test(freshVoice.transcript)) {
      trigger = { kind: "unfinished_thread", detail: `Recent voice memo: "${freshVoice.transcript.slice(0, 160)}". Build a follow-up on that action.` };
    }
    // 2) Fresh person signal detected by the Pyng watcher — ALWAYS preferred
    //    over stale recent_milestones entries (which can be years old).
    if (!trigger) {
      const sig = pinnedSignal ?? canonicalReason;
      if (sig && typeof sig.summary === "string" && sig.summary.trim().length > 10) {
        const kindLabel = String(sig.kind).replace(/[_-]+/g, " ");
        // Only a real, recent publication date licenses a signal-led note. An
        // undated item (or one whose text names an old year) is history: fall
        // through to cadence/context rather than implying it just happened.
        const published = sig.published_at ? Date.parse(sig.published_at) : NaN;
        const dated =
          Number.isFinite(published) &&
          Date.now() - published <= 60 * 86400000 &&
          !mentionsStaleYear(sig.summary);
        if (dated) {
          trigger = {
            kind: "fresh_signal",
            detail: `THE SUBJECT OF THIS NOTE, and the only news you may reference: a ${kindLabel} published ${new Date(published).toLocaleDateString()} — "${String(sig.summary).slice(0, 240)}". Open on this specific item and nothing else. Website snippets, tracked-page changes and older milestones below are background only: do NOT write the note about them. Do not invent details beyond this summary.`,
          };
          subjectHeadline = String(sig.summary).slice(0, 240);
        }
      }
    }
    // 3) Fresh link change detected by the watcher (preferred). Falls back to
    //    "we have a snippet at all" as a weaker signal.
    if (!trigger) {
      const fresh = ((freshChanges ?? []) as any[])[0];
      if (
        fresh &&
        typeof fresh.change_summary === "string" &&
        fresh.change_summary.trim().length > 30 &&
        !mentionsStaleYear(fresh.change_summary)
      ) {
        const matchedLink = (linkRows as any[] | null)?.find((l) => l.url === fresh.url);
        const kind = matchedLink?.kind ?? "link";
        trigger = {
          kind: "fresh_link",
          detail: `New on their ${kind} (${new Date(fresh.fetched_at).toLocaleDateString()}): ${String(fresh.change_summary).slice(0, 240)}. Reference it naturally — "saw this", "thought of you".`,
        };
      } else {
        const withSnippet = linkSnippets.find((l) => l.snippet && l.snippet.trim().length > 60);
        if (withSnippet) {
          trigger = { kind: "fresh_link", detail: `From their ${withSnippet.kind}: ${withSnippet.snippet!.slice(0, 200)}.` };
        }
      }
    }
    // 3b) Recent milestone — only if it actually looks recent (≤90d). A stale
    //     2-year-old milestone shouldn't outrank a freshly-discovered signal,
    //     and shouldn't drive a brand-new draft on its own.
    if (!trigger && milestones.length) {
      const ninetyDaysAgo = now - 90 * 86400000;
      const recentM = milestones.find((m: any) => {
        if (typeof m === "string") return false; // no date → assume stale
        // A context copy must never resurrect a signal the user dismissed.
        if (typeof m?.source_url === "string" && dismissedSignalSources.has(m.source_url)) return false;
        const dateStr = m?.occurred_on || m?.detected_at || m?.date;
        if (!dateStr) return false;
        const t = new Date(dateStr).getTime();
        return Number.isFinite(t) && t >= ninetyDaysAgo;
      });
      if (recentM) {
        const mText = recentM?.title || recentM?.text || recentM?.summary || "";
        if (mText) trigger = { kind: "recent_milestone", detail: `Their recent milestone: ${String(mText).slice(0, 160)}.` };
      }
    }
    // 4) Hook-driven trigger from /today card reason — honor why the card was surfaced
    if (!trigger && data.hookTrigger) {
      const det = (data.hookTriggerDetail ?? "").slice(0, 200);
      switch (data.hookTrigger) {
        case "fresh_signal":
          if (det) {
            trigger = {
              kind: "fresh_signal",
              detail: `Recent signal about them: ${det}. Reference it naturally — do not invent details beyond this summary.`,
            };
          }
          break;
        case "birthday":
          trigger = {
            kind: "birthday",
            detail: `Their birthday is ${det || "coming up"}. Acknowledge it warmly in one line — don't overdo it, no clichés like "hope you have an amazing year".`,
          };
          break;
        case "trip":
          trigger = {
            kind: "trip_overlap",
            detail: `You'll be in ${det || "their city"} soon. Mention it directly and offer to grab coffee or a quick meet — keep it low-pressure.`,
          };
          break;
        case "new_contact":
          trigger = {
            kind: "new_intro",
            detail: `Recently added to the user's Pyng network${det ? ` — ${det}` : ""}. They already know each other, so do NOT introduce the sender. Open with a low-pressure, familiar hello. Do not invent specific shared history.`,
          };
          break;
        // vip_drift / drift fall through to cadence_due below
      }
    }
    // 5) Mutual interest
    if (!trigger && interests.length) {
      trigger = { kind: "mutual_interest", detail: `Shared interest: ${interests.slice(0, 3).join(", ")}. Reference a small relevant thought or question.` };
    }
    // 6) Cadence due
    if (!trigger && Number.isFinite(daysSinceLast) && daysSinceLast >= cadenceDays) {
      trigger = { kind: "cadence_due", detail: `It's been ${daysSinceLast} days since last touch (cadence ${cadenceDays}d). Humble reconnection.` };
    }
    if (!trigger) {
      trigger = { kind: "check_in", detail: "No strong signal — a brief, humble check-in. Do not invent specifics." };
    }
    // No recorded interaction ever: whatever the trigger is, this is still a
    // first message. Force an introduction rather than assumed familiarity.
    if (!contact.last_interaction_at) {
      trigger = {
        ...trigger,
        detail: `${trigger.detail} There is no logged history with them in the app — avoid referencing specific past conversations, but they already know the sender: no self-introduction, no cold-outreach framing.`,
      };
    }

    // Signal hash: cache key based on inputs that affect the draft.
    const signal = JSON.stringify({
      tone,
      lit: contact.last_interaction_at ?? null,
      mids: memList.map((m) => m.id),
      iids: (ints ?? []).map((i: any) => i.id),
      ctx: (ctx as any)?.updated_at ?? null,
      groups,
      lids: (linkRows ?? []).map((l: any) => l.id),
      vids: (voiceRows ?? []).map((v: any) => v.id),
      interests,
      lastSent: lastSent ? `${lastSent.slice(0, 60)}|${(contact as any).last_sent_at ?? ""}` : null,
      trigger: trigger.kind,
      sids: pinnedSignal ? [`signal:${pinnedSignal.id}`] : canonicalReason ? [canonicalReason.id] : [],
      sender: `${sender.name ?? ""}|${sender.role ?? ""}`,
      tsam: toneSamples.length ? createHash("sha1").update(toneSamples.join("\u0000")).digest("hex").slice(0, 12) : null,
    });
    const signalHash = createHash("sha1").update(signal).digest("hex");

    const cachedAt = (contact as any).cached_note_at ? new Date((contact as any).cached_note_at).getTime() : 0;
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const cacheFresh = cachedAt > 0 && (Date.now() - cachedAt) < sevenDays;
    if (
      cacheFresh &&
      (contact as any).cached_note_signal_hash === signalHash &&
      typeof (contact as any).cached_note === "string" &&
      (contact as any).cached_note.length > 0
    ) {
      const cachedMeta = (contact as any).cached_note_meta as any;
      return {
        note: (contact as any).cached_note,
        empty: false,
        cached: true,
        why_now: cachedMeta?.why_now ?? "",
        confidence: cachedMeta?.confidence ?? 0.6,
        trigger: trigger.kind,
        canonical_reason_id: canonicalReason?.id ?? null,
        subject_headline: subjectHeadline,
      };
    }

    let note = "";
    let why_now = "";
    let confidence = 0.5;
    let alt: string | undefined;
    try {
      const out = await draftNextTouchNote({
        contact: contact as any,
        context_card: ctx ?? null,
        memories: memList.map((m) => m.fact),
        interactions: (ints ?? []) as any,
        tone,
        groups,
        links: linkSnippets,
        recent_voice_memos: voiceMemos,
        interests,
        last_sent_note: lastSent,
        last_sent_at: (contact as any).last_sent_at ?? null,
        sender,
        tone_samples: toneSamples,
        trigger,
      });
      note = out.note;
      why_now = out.why_now;
      confidence = out.confidence;
      alt = out.alt;
    } catch (err) {
      // A model failure must never render as "no draft". Fall back to the
      // stored note and tell the client it's a stale fallback so it can retry.
      const message = err instanceof Error ? err.message : String(err);
      console.error("[getNextTouchNote] draft failed", {
        contactId: data.contactId,
        trigger: trigger.kind,
        pinned: usePinned,
        message: message.slice(0, 300),
      });
      const fallback = typeof (contact as any).cached_note === "string" ? (contact as any).cached_note : "";
      return {
        note: fallback,
        empty: !fallback,
        error: true,
        stale: !!fallback,
        trigger: trigger.kind,
        canonical_reason_id: canonicalReason?.id ?? null,
        subject_headline: subjectHeadline,
      };
    }

    if (note) {
      await supabase
        .from("contacts")
        .update({
          cached_note: note,
          cached_note_tone: tone,
          cached_note_signal_hash: signalHash,
          cached_note_at: new Date().toISOString(),
        })
        .eq("id", data.contactId)
        .eq("user_id", userId);
    }

    return {
      note,
      empty: !note,
      why_now,
      confidence,
      alt,
      trigger: trigger.kind,
      trigger_detail: trigger.detail,
      canonical_reason_id: canonicalReason?.id ?? null,
        subject_headline: subjectHeadline,
    };
  });

export const setContactTone = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({
    contactId: z.string().uuid(),
    tone: z.union([toneSchema, z.enum(["casual", "warm", "neutral", "polished", "professional"])]),
  }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const tone = normalizeTone(data.tone);
    const { error } = await supabase
      .from("contacts")
      .update({ tone_preference: tone })
      .eq("id", data.contactId)
      .eq("user_id", userId);
    if (error) throw error;
    return { ok: true };
  });

export const recordSentNote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({
    contactId: z.string().uuid(),
    note: z.string().min(1).max(2000),
    channel: z.enum(["email", "sms", "whatsapp", "linkedin", "call", "in_person", "other"]).optional(),
  }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const now = new Date().toISOString();
    const { logTouch: writeTouch } = await import("./touch-log.server");
    const channel = data.channel ?? "other";
    const { error } = await supabase
      .from("contacts")
      .update({
        last_sent_note: data.note,
        last_sent_at: now,
      })
      .eq("id", data.contactId)
      .eq("user_id", userId);
    if (error) throw error;
    // Self-reported: we handed the message off, we didn't watch it leave.
    // Mail sync upgrades this row to `confirmed` if Gmail sees it.
    await writeTouch(supabase, {
      userId,
      contactId: data.contactId,
      channel,
      source: "reported",
      summary: `Sent: ${data.note.slice(0, 200)}`,
      occurredAt: now,
    });
    return { ok: true, at: now };
  });

/**
 * "I already reached out" — a touch that happened outside the app (dinner, a
 * call, a LinkedIn DM). Resets the person's clock exactly like Send does so
 * the Today feed stops nagging about them.
 */
export const logTouch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({
    contactId: z.string().uuid(),
    occurredAt: z.string().datetime().optional(),
    channel: z.enum(["email", "meeting", "call", "note"]).optional(),
    summary: z.string().max(300).optional(),
  }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const at = data.occurredAt ?? new Date().toISOString();
    const type = data.channel ?? "note";
    const { logTouch: writeTouch } = await import("./touch-log.server");
    // Told to us after the fact: a meeting or call you had is as good as
    // confirmed; a note about "I messaged them" stays self-reported.
    await writeTouch(supabase, {
      userId,
      contactId: data.contactId,
      channel: type === "meeting" ? "in_person" : type === "call" ? "call" : type === "email" ? "email" : "other",
      source: type === "meeting" || type === "call" ? "confirmed" : "reported",
      type: type as any,
      summary: data.summary?.trim() || "Reached out (logged manually)",
      occurredAt: at,
    });
    return { ok: true, at };
  });

export const setContactInterests = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({
    contactId: z.string().uuid(),
    interests: z.array(z.string().min(1).max(120)).max(20),
  }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const cleaned = Array.from(new Set(data.interests.map((s) => s.trim()).filter((s) => s.length > 0))).slice(0, 20);
    // Upsert contact_context row.
    const { data: existing } = await supabase
      .from("contact_context")
      .select("contact_id")
      .eq("contact_id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    if (existing) {
      const { error } = await supabase
        .from("contact_context")
        .update({ interests: cleaned, updated_at: new Date().toISOString() })
        .eq("contact_id", data.contactId)
        .eq("user_id", userId);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from("contact_context")
        .insert({ contact_id: data.contactId, user_id: userId, interests: cleaned });
      if (error) throw error;
    }
    return { ok: true, interests: cleaned };
  });

// ---- Meet-your-orbit (onboarding magic moment) ----

export const getMeetOrbitCard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const [
      { data: contact },
      { data: ctx },
      { data: links },
      { data: cands },
    ] = await Promise.all([
      supabase
        .from("contacts")
        .select("id, name, photo_url, title, company, location")
        .eq("user_id", userId)
        .eq("id", data.contactId)
        .maybeSingle(),
      supabase
        .from("contact_context")
        .select("role_title, current_company, passions, active_projects, recent_milestones, partner_name, kids, pets, source_urls")
        .eq("contact_id", data.contactId)
        .maybeSingle(),
      supabase
        .from("contact_links")
        .select("kind, url")
        .eq("contact_id", data.contactId),
      supabase
        .from("contact_match_candidates")
        .select("candidates, confidence")
        .eq("contact_id", data.contactId)
        .eq("user_id", userId)
        .maybeSingle(),
    ]);
    if (!contact) throw new Error("Contact not found");
    return {
      contact: {
        id: contact.id,
        name: contact.name,
        photo_url: contact.photo_url,
        title: (contact as any).title ?? ctx?.role_title ?? null,
        company: (contact as any).company ?? ctx?.current_company ?? null,
        location: (contact as any).location ?? null,
      },
      context: ctx
        ? {
            passions: (ctx.passions as string[]) ?? [],
            active_projects: (ctx.active_projects as string[]) ?? [],
            recent_milestones: (ctx.recent_milestones as string[]) ?? [],
            partner_name: ctx.partner_name ?? null,
            kids: (ctx.kids as any[]) ?? [],
            pets: (ctx.pets as any[]) ?? [],
            source_count: Array.isArray(ctx.source_urls) ? ctx.source_urls.length : 0,
          }
        : null,
      links: (links ?? []).map((l: any) => ({ kind: l.kind as string, url: l.url as string })),
      candidates: cands?.candidates as Array<{ kind: string; url: string; title?: string; why?: string }> | null,
      confidence: (cands?.confidence as number | null) ?? null,
    };
  });

// ---- Birthday guess: accept / dismiss ----

export const acceptBirthdayGuess = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: ctx } = await supabase
      .from("contact_context")
      .select("birthday_guess")
      .eq("contact_id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    const guess = (ctx as any)?.birthday_guess as string | null | undefined;
    if (!guess) return { ok: false as const, reason: "no-guess" as const };
    await supabase
      .from("contacts")
      .update({ birthday: guess })
      .eq("id", data.contactId)
      .eq("user_id", userId);
    await supabase
      .from("contact_context")
      .update({ birthday_guess: null, birthday_guess_source: null })
      .eq("contact_id", data.contactId)
      .eq("user_id", userId);
    return { ok: true as const, birthday: guess };
  });

export const dismissBirthdayGuess = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    await supabase
      .from("contact_context")
      .update({ birthday_guess: null, birthday_guess_source: null })
      .eq("contact_id", data.contactId)
      .eq("user_id", userId);
    return { ok: true as const };
  });

/**
 * Lightweight status of the background monitoring pipeline for the signed-in
 * user. Powers the small "Last scanned …" line under "More for today".
 */
export const getScanStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const sevenAgoIso = new Date(Date.now() - 7 * 86400000).toISOString();
    const dayAgoIso = new Date(Date.now() - 86400000).toISOString();
    const [
      { data: lastSnap },
      { count: signals7d },
      { count: tracked },
      { count: disabled },
      { data: lastRun },
      { count: signals24h },
      { count: opps24h },
      { count: opps7d },
    ] = await Promise.all([
      supabase
        .from("link_snapshots")
        .select("fetched_at")
        .eq("user_id", userId)
        .order("fetched_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("person_signals")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .gte("detected_at", sevenAgoIso),
      supabase
        .from("contact_links")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .is("disabled_at", null),
      supabase
        .from("contact_links")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .not("disabled_at", "is", null),
      supabase
        .from("scan_runs")
        .select("job_name, finished_at, started_at, stats")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("person_signals")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .gte("detected_at", dayAgoIso),
      // Opportunities created from company events / topic scans. `person_signal_id`
      // is null for these, so counting them alongside person_signals doesn't
      // double-count the same underlying signal. Only count ones attached to a
      // person in the orbit — topic-only rows never surface as cards.
      supabase
        .from("opportunities")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .is("person_signal_id", null)
        .not("contact_id", "is", null)
        .gte("created_at", dayAgoIso),
      supabase
        .from("opportunities")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .is("person_signal_id", null)
        .not("contact_id", "is", null)
        .gte("created_at", sevenAgoIso),
    ]);
    const stats = (lastRun?.stats ?? {}) as Record<string, number>;
    const lastRunAt = lastRun?.finished_at ?? lastRun?.started_at ?? null;
    const newLast24h = (signals24h ?? 0) + (opps24h ?? 0);
    const newLast7d = (signals7d ?? 0) + (opps7d ?? 0);
    return {
      lastScannedAt: lastSnap?.fetched_at ?? null,
      signalsLast7d: signals7d ?? 0,
      linksTracked: tracked ?? 0,
      linksDisabled: disabled ?? 0,
      lastRunAt,
      lastRunJob: lastRun?.job_name ?? null,
      lastRunScanned: stats.scanned ?? 0,
      lastRunPromoted: stats.promoted ?? 0,
      newLast24h,
      newLast7d,
      scanOverdue: lastRunAt
        ? Date.now() - new Date(lastRunAt).getTime() > 26 * 3600000
        : true,
    };
  });

/**
 * Per-job scan health. `getScanStatus` answers "is the feed fresh?"; this
 * answers "is every job in the pipeline actually running?" — the question the
 * audit could only answer by reading the database by hand.
 */
export const getScanJobHealth = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const JOBS = [
      { name: "refresh-link-snapshots", label: "Read watched pages", maxAgeHours: 26 },
      { name: "monitor-people", label: "Search news per person", maxAgeHours: 26 },
      { name: "monitor-companies", label: "Company news", maxAgeHours: 26 },
      { name: "monitor-topics", label: "Interest topics", maxAgeHours: 26 },
      { name: "synthesize-opportunities", label: "Build the feed", maxAgeHours: 26 },
      { name: "detect-relationship-events", label: "Birthdays & follow-ups", maxAgeHours: 26 },
      { name: "expire-opportunities", label: "Retire stale cards", maxAgeHours: 26 },
    ];

    const nowIso = new Date().toISOString();
    // Scan rows are admin-only under RLS and the frequent jobs (sweep, calendar
    // sync) drown out the daily ones in any fixed window — so read the latest
    // run per job name with the service client. Nothing user-scoped is returned.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [runResults, { count: dueLinks }, { count: neverScanned }, { data: profileRow }] = await Promise.all([
      Promise.all(
        JOBS.map((j) =>
          supabaseAdmin
            .from("scan_runs")
            .select("job_name, started_at, finished_at, ok, error, stats")
            .eq("job_name", j.name)
            .order("started_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
        ),
      ),
      supabase
        .from("contact_links")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("monitorable", true)
        .is("disabled_at", null)
        .lte("next_check_at", nowIso),
      supabase
        .from("contacts")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("is_stub", false)
        .is("last_scan_at", null),
      supabase.from("profiles").select("created_at").eq("id", userId).maybeSingle(),
    ]);

    const byJob = new Map<string, any>();
    for (const res of runResults) {
      const r = (res as any)?.data;
      if (r) byJob.set(r.job_name, r);
    }

    const jobs = JOBS.map((j) => {
      const last = byJob.get(j.name);
      const at = last?.finished_at ?? last?.started_at ?? null;
      const ageMs = at ? Date.now() - new Date(at).getTime() : null;
      return {
        name: j.name,
        label: j.label,
        lastRunAt: at,
        ok: last ? last.ok !== false : false,
        error: last?.error ?? null,
        stats: (last?.stats ?? {}) as Record<string, number>,
        neverRun: at === null,
        stale: ageMs !== null && ageMs > j.maxAgeHours * 3600_000,
      };
    });

    const createdAt = (profileRow as any)?.created_at
      ? new Date((profileRow as any).created_at).getTime()
      : null;
    const warmingUp = createdAt !== null && Date.now() - createdAt < 26 * 3600_000;

    return {
      jobs,
      dueLinkBacklog: dueLinks ?? 0,
      contactsNeverScanned: neverScanned ?? 0,
      warmingUp,
      healthy: jobs.every((j) => !j.stale && (j.neverRun ? warmingUp : j.ok)),
    };
  });

/**
 * Email preferences. Only optional categories are switchable — account and
 * access email always sends, so nobody can lock themselves out of Pyng.
 */
export const setEmailPreferences = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        weeklyBrief: z.boolean().optional(),
        signalAlerts: z.boolean().optional(),
        productTips: z.boolean().optional(),
        /** Days to hold optional email; 0 resumes immediately. */
        pauseDays: z.number().int().min(0).max(90).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch: Record<string, unknown> = {};
    if (data.weeklyBrief !== undefined) patch["weekly_email_enabled"] = data.weeklyBrief;
    if (data.signalAlerts !== undefined) patch["email_signal_alerts"] = data.signalAlerts;
    if (data.productTips !== undefined) patch["email_product_tips"] = data.productTips;
    if (data.pauseDays !== undefined) {
      patch["email_paused_until"] =
        data.pauseDays === 0
          ? null
          : new Date(Date.now() + data.pauseDays * 86400_000).toISOString();
    }
    if (!Object.keys(patch).length) return { ok: true };
    const { error } = await supabase.from("profiles").update(patch as any).eq("id", userId);
    if (error) throw error;
    return { ok: true, ...patch };
  });

/**
 * "Actually, I didn't send that." Removes the most recent self-reported touch
 * for a contact (last 10 minutes only) and rewinds their last-touch clock so
 * they return to the feed. Confirmed touches are never removed this way.
 */
export const undoLastReportedTouch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const cutoff = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    const { data: recent } = await supabase
      .from("interactions")
      .select("id")
      .eq("user_id", userId)
      .eq("contact_id", data.contactId)
      .eq("source", "reported")
      .gte("created_at", cutoff)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!recent) return { ok: false, removed: false };

    await supabase.from("interactions").delete().eq("id", (recent as any).id).eq("user_id", userId);

    // Rewind to whatever touch is now the most recent one, if any.
    const { data: prior } = await supabase
      .from("interactions")
      .select("occurred_at, channel, source")
      .eq("user_id", userId)
      .eq("contact_id", data.contactId)
      .order("occurred_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    await supabase
      .from("contacts")
      .update({
        last_interaction_at: (prior as any)?.occurred_at ?? null,
        last_touch_channel: (prior as any)?.channel ?? null,
        last_touch_source: (prior as any)?.source ?? null,
      })
      .eq("id", data.contactId)
      .eq("user_id", userId);
    return { ok: true, removed: true };
  });
